import { useState, useEffect, useRef } from 'react';
import { listOrgCollection, readOrgFile, writeOrgFile } from '../../storage/FileSystemAdapter';
import ImportWizard from '../shared/ImportWizard';
import PeopleSelector from '../shared/PeopleSelector';
import EvidenceSelector from '../shared/EvidenceSelector';
import ConfirmDialog from '../shared/ConfirmDialog';
import Tooltip from '../shared/Tooltip';
import { COUNTRIES } from '../../utils/countries';
import { SUBREGIONS } from '../../utils/subregions';
import { maybeAdvancePdcaStage } from '../../utils/pdcaStage';
import { priorEditableStateDescriptor } from '../../utils/changeLog';
import { BG_CONTENT, BG_ACTIVE, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, BTN_STATUS_FLAG, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, BG_SHELL, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, FONT_LIST_ITEM } from '../../theme';
import { SUPPORTED_NORMS, NORM_GDPR, NORM_NIS2, ORGANIZATION_TYPES, PUBLIC_SECTOR_EXPOSURE_VALUES } from '../../utils/enums';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:               'Configuración',
    save:                'Guardar',
    saving:              'Guardando…',
    saveOk:              '✓ Guardado',
    saveError:           'Error al guardar',
    savePeople:          'Guardar personas',
    unsavedLabel:        'Cambios sin guardar',
    unsavedConfirm:      'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving:  'Salir sin guardar',
    saveAndLeave:        'Guardar y salir',
    loading:             'Cargando…',
    // Section 1 — Organization
    fName:               'Nombre',
    fSector:             'Sector',
    fSize:               'Empleados (aprox.)',
    fCountry:            'País',
    fLanguage:           'Idioma por defecto de la organización',
    orgLanguageTooltip:  'Usado en el Executive Dashboard y como contexto en prompts de IA. El selector de idioma del encabezado controla el idioma de la interfaz.',
    fReviewPeriod:       'Período de revisión de documentos (meses)',
    fPdcaStage:           'Fase PDCA actual',
    pdcaStageOptions:     { Plan: 'Plan', Do: 'Hacer', Check: 'Verificar', Act: 'Actuar' },
    fMrFrequency:         'Frecuencia revisión por dirección (meses)',
    fAuditFrequency:      'Frecuencia auditoría interna (meses)',
    fRiskThresholdLow:    'Umbral de riesgo bajo',
    fRiskThresholdMedium: 'Umbral de riesgo medio',
    fRiskAcceptance:      'Umbral de aceptación de riesgo',
    riskAcceptanceOptions: { LOW: 'Bajo', MEDIUM: 'Medio', HIGH: 'Alto' },
    fConsultantPerson:    'Consultor responsable (tú)',
    fConsultantPersonHelper: 'Se usa para registrar quién realiza los cambios en el sistema.',
    fOrganizationType:                        'Tipo de organización',
    organizationTypeOptions:                  { public: 'Pública', private: 'Privada', mixed: 'Mixta' },
    fPublicSectorExposure:                    'Exposición al sector público',
    publicSectorExposureOptions:              { none: 'Sin exposición', contractor: 'Contratista del sector público', critical_service_provider: 'Proveedor de servicios críticos al sector público', mixed_subordinate: 'Sociedad mixta subordinada a entidad pública' },
    fPublicSectorExposureDescription:         'Descripción de la exposición',
    fPublicSectorExposureDescriptionPlaceholder: 'Ej: contratista de la AGE en categoría MEDIA del ENS; presta servicios al SERMAS; pertenece a grupo con matriz pública municipal…',
    fPublicSectorExposureDescriptionHelper:   'Texto libre. Antes de guardar se eliminan automáticamente el nombre de la organización, el nombre del consultor responsable y cualquier dirección de correo electrónico para proteger información sensible al enviar al servicio de IA.',
    // Section — ISMS Roles
    fIsmsRoleTopMgmt:      'Firmante de alta dirección',
    fIsmsRoleIsmsManager:  'Responsable del SGSI',
    fIsmsRoleCiso:         'CISO',
    fCisoSameAsIsmsManager: 'Mismo que Responsable del SGSI',
    fIsmsRoleDpo:          'Delegado de protección de datos (DPO)',
    fIsmsRoleNis2Contact:  'Contacto NIS2',
    fTopMgmtCommitmentEvidence:     'Evidencia de compromiso de la dirección',
    fTopMgmtCommitmentEvidenceHelp: 'Acta, comunicado o registro que demuestra el compromiso de la alta dirección con el SGSI (cláusula 5.1).',
    // Section — Reference prefixes
    fRefPrefixRisk:             'Riesgo',
    fRefPrefixAsset:            'Activo',
    fRefPrefixAuthority:        'Autoridad',
    fRefPrefixSpecialInterestGroup: 'Grupo de interés',
    fRefPrefixSupplier:         'Proveedor',
    fRefPrefixCustomer:         'Cliente',
    fRefPrefixIncident:         'Incidente',
    fRefPrefixAudit:            'Auditoría',
    fRefPrefixManagementReview: 'Revisión por dirección',
    fRefPrefixNonconformity:    'No conformidad',
    fRefPrefixObjective:        'Objetivo',
    fRefPrefixDocument:         'Documento',
    fRefPrefixMetric:           'Métrica',
    fRefPrefixEvidence:         'Evidencia',
    fRefPrefixLegalRegister:    'Registro legal',
    // Section 2 — Certification
    fCertStatus:         'Estado de certificación',
    certStatusOptions:   {
      none:         'Sin certificación',
      preparing:    'Preparando certificación',
      stage1:       'Auditoría Fase 1',
      stage2:       'Auditoría Fase 2',
      certified:    'Certificado',
      surveillance: 'Auditoría de seguimiento',
      expired:      'Certificado vencido',
    },
    fCertBody:           'Organismo de certificación',
    fCertNumber:         'Número de certificado',
    fStage1Date:         'Fecha auditoría Fase 1',
    fStage2Date:         'Fecha auditoría Fase 2',
    fCertDate:           'Fecha de certificación',
    fRecertDate:         'Fecha de recertificación',
    // Section 3 — Documents
    fDefaultClass:            'Clasificación por defecto',
    fDefaultClassEmpty:       'Define primero los niveles de clasificación',
    fDefaultClassPlaceholder: 'Seleccionar…',
    fDocClass:           'Niveles de clasificación',
    addBtn:              'Añadir',
    addClassPlaceholder: 'Nuevo nivel…',
    // Section 4 — Technologies
    addTechPlaceholder:  'AWS, Microsoft 365, GitHub…',
    // Section — Jurisdictions
    fRegions:               'Jurisdicciones',
    fRegionsHint:           'Solo jurisdicciones con obligaciones legales directas; no donde solo haya teletrabajadores individuales o clientes sin presencia operativa.',
    addRegionPlaceholder:   'Cataluña, Euskadi, California, Bayern…',
    // Section 5 — AI Context
    fAiProcessingConsent:     'Consentimiento para el procesamiento por IA',
    fAiProcessingConsentDate: 'Fecha del consentimiento',
    aiProcessingConsentHelper:
      'Permite el uso de IA para generar documentos y sugerencias en este SGSI. Al desactivarlo se desactivan las funciones de IA.',
    fAiContext:          'Contexto para la IA',
    fAiContextHelper:    'Texto que se incluirá en los prompts de IA para personalizar los documentos generados. Incluye aquí el alcance del SGSI y cualquier contexto relevante para la organización. No incluyas nombres de personas, clientes ni datos identificativos.',
    fAiContextPlaceholder: 'Ej. Empresa de tecnología con 50-100 empleados. SGSI cubre desarrollo y operaciones de plataforma SaaS. Tecnologías principales: AWS, GitHub, Microsoft 365. Cumplimiento aplicable: GDPR, NIS2.',
    // Section — Awareness coverage policy
    fAwarenessPolicyEnable: 'Definir una política de cobertura aplicable a toda la organización',
    fAwarenessTarget:       'Cobertura objetivo (%)',
    fAwarenessThresholds:   'Umbrales RAG (%)',
    fAwarenessGreen:        'Verde ≥',
    fAwarenessAmber:        'Ámbar ≥',
    fAwarenessRed:          'Rojo <',
    fAwarenessRedReadonly:  'Por debajo de ámbar',
    helpAwarenessPolicy:    'Define cómo se evalúa la cobertura del programa de concienciación. Los cambios se aplican inmediatamente a todos los programas, incluidos los de años pasados — los números no reescriben la historia, solo cómo se compara con la política vigente. Sin política definida, el módulo de Concienciación se comporta como hasta ahora: porcentajes simples, sin semáforo y sin alerta.',
    // Section 6 — People
    addPerson:           'Añadir persona',
    editPerson:          'Editar',
    deactivate:          'Desactivar',
    reactivate:          'Reactivar',
    savePerson:          'Guardar',
    cancelPerson:        'Cancelar',
    fPersonName:         'Nombre',
    fPersonEmail:        'Email',
    fPersonRole:         'Rol',
    fPersonDept:         'Departamento',
    fPersonCountry:      'País',
    fPersonCity:         'Ciudad',
    fPersonTimezone:     'Zona horaria',
    emailRequired:       'El email es obligatorio.',
    emailDuplicate:      'Este email ya está registrado en otra persona.',
    timezoneWarning:     'Formato de zona horaria no reconocido. Se recomienda usar el formato IANA (ej. Europe/Madrid).',
    colName:             'Nombre',
    colRole:             'Rol',
    colDept:             'Departamento',
    colCountry:          'País',
    colStatus:           'Estado',
    colActions:          'Acciones',
    statusActive:        'Activo',
    statusInactive:      'Inactivo',
    noPeople:            'No hay personas registradas.',
    nameRequired:        'El nombre es obligatorio.',
    newPersonTitle:      'Nueva persona',
    editPersonTitle:     'Editar persona',
    deletePerson:        'Eliminar',
    deletePersonConfirm: '¿Seguro que quieres eliminar a "{name}"? Asegúrate de que no está asignado a ningún registro existente antes de eliminarlo.',
    deleteBtn:           'Eliminar',
    selectAll:           'Seleccionar todo',
    nSelected:           '{n} seleccionados',
    deleteSelected:      'Eliminar seleccionados',
    deactivateSelected:  'Desactivar seleccionados',
    reactivateSelected:  'Reactivar seleccionados',
    deleteSelectedConfirm: '¿Eliminar a las siguientes personas?\n\n{names}\n\nAsegúrate de que no están asignadas a ningún registro existente.',
    deactivateSelectedConfirm: '¿Desactivar a las siguientes personas?\n\n{names}\n\nSe marcarán como inactivas. Puedes ajustar la fecha de fin en cada registro.',
    reactivateSelectedConfirm: '¿Reactivar a las siguientes personas?\n\n{names}',
    importCsv:             'Importar CSV',
    importPeopleTitle:     'Importar personas',
    formCancel:            'Cancelar',
    fPersonType:           'Tipo',
    personTypeOptions:     { employee: 'Empleado', contractor: 'Contratista', external: 'Externo' },
    fStartDate:            'Fecha de inicio',
    fEndDate:              'Fecha de fin',
    fIsResponsibilityRole: 'Rol de responsabilidad',
    // Section — Reopen records (§11 rule 35)
    reopenHeading:         'Reabrir registros cerrados',
    reopenHelper:          'Los registros en estado terminal (NC cerrada, incidente cerrado, auditoría completada, revisión por la dirección aprobada, documento retirado/reemplazado) son inmutables. Usa esta sección para devolver un registro a un estado editable. Cada reapertura queda registrada en el changeLog con el motivo indicado.',
    reopenNcGroup:         'No conformidades cerradas',
    reopenIncGroup:        'Incidentes cerrados',
    reopenAudGroup:        'Auditorías completadas',
    reopenMrGroup:         'Revisiones por la dirección aprobadas',
    reopenDocGroup:        'Documentos retirados/reemplazados',
    reopenEmptyGroup:      'No hay registros en estado terminal.',
    reopenLoading:         'Cargando registros…',
    reopenBtn:             'Reabrir',
    reopenModalTitle:      'Reabrir registro',
    reopenModalHelper:     'Indica el motivo de la reapertura. Se guardará en el changeLog del registro.',
    reopenReasonLabel:     'Motivo',
    reopenReasonRequired:  'El motivo es obligatorio.',
    reopenConfirmBtn:      'Confirmar reapertura',
    reopenCancelBtn:       'Cancelar',
    reopenError:           'No se pudo reabrir el registro. Inténtalo de nuevo.',
    // §11 rule 36 — person delete reference scan
    deletePersonBlockedMsg:   'La persona "{name}" está referenciada en registros existentes y no puede eliminarse. ¿Desactivarla en su lugar?',
    deleteSelectedBlockedMsg: '{blocked} de {total} personas seleccionadas están referenciadas en registros existentes. Para proteger la integridad de datos, ninguna de las {total} será eliminada. ¿Desactivar las {total} en su lugar?',
    deactivateInstead:        'Desactivar',
    scanningPeople:           'Verificando referencias…',
  },
  en: {
    title:               'Settings',
    save:                'Save',
    saving:              'Saving…',
    saveOk:              '✓ Saved',
    saveError:           'Save error',
    savePeople:          'Save people',
    unsavedLabel:        'Unsaved changes',
    unsavedConfirm:      'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving:  'Leave without saving',
    saveAndLeave:        'Save and leave',
    loading:             'Loading…',
    // Section 1 — Organization
    fName:               'Name',
    fSector:             'Sector',
    fSize:               'Employees (approx.)',
    fCountry:            'Country',
    fLanguage:           'Default organization language',
    orgLanguageTooltip:  'Used in the Executive Dashboard and as context in AI prompts. The language selector in the header controls the interface language.',
    fReviewPeriod:       'Document review period (months)',
    fPdcaStage:           'Current PDCA stage',
    pdcaStageOptions:     { Plan: 'Plan', Do: 'Do', Check: 'Check', Act: 'Act' },
    fMrFrequency:         'Management review frequency (months)',
    fAuditFrequency:      'Internal audit frequency (months)',
    fRiskThresholdLow:    'Low risk threshold',
    fRiskThresholdMedium: 'Medium risk threshold',
    fRiskAcceptance:      'Risk acceptance threshold',
    riskAcceptanceOptions: { LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High' },
    fConsultantPerson:    'Responsible consultant (you)',
    fConsultantPersonHelper: 'Used to track who makes changes in the system.',
    fOrganizationType:                        'Organization type',
    organizationTypeOptions:                  { public: 'Public', private: 'Private', mixed: 'Mixed' },
    fPublicSectorExposure:                    'Public sector exposure',
    publicSectorExposureOptions:              { none: 'No exposure', contractor: 'Public-sector contractor', critical_service_provider: 'Critical service provider to public sector', mixed_subordinate: 'Mixed group subordinate to public entity' },
    fPublicSectorExposureDescription:         'Exposure description',
    fPublicSectorExposureDescriptionPlaceholder: 'E.g. central-government contractor under Spain ENS MEDIUM category; provides services to a regional health authority; member of a holding group whose parent is a municipal public entity…',
    fPublicSectorExposureDescriptionHelper:   'Free text. Before saving, the organization name, the responsible consultant name, and any email addresses are automatically stripped to protect sensitive information when sent to the AI service.',
    // Section — ISMS Roles
    fIsmsRoleTopMgmt:      'Top management signatory',
    fIsmsRoleIsmsManager:  'ISMS Manager',
    fIsmsRoleCiso:         'CISO',
    fCisoSameAsIsmsManager: 'Same as ISMS Manager',
    fIsmsRoleDpo:          'Data protection officer (DPO)',
    fIsmsRoleNis2Contact:  'NIS2 contact',
    fTopMgmtCommitmentEvidence:     'Top management commitment evidence',
    fTopMgmtCommitmentEvidenceHelp: 'Minutes, statement or record evidencing top management commitment to the ISMS (clause 5.1).',
    // Section — Reference prefixes
    fRefPrefixRisk:             'Risk',
    fRefPrefixAsset:            'Asset',
    fRefPrefixAuthority:        'Authority',
    fRefPrefixSpecialInterestGroup: 'Special interest group',
    fRefPrefixSupplier:         'Supplier',
    fRefPrefixCustomer:         'Customer',
    fRefPrefixIncident:         'Incident',
    fRefPrefixAudit:            'Audit',
    fRefPrefixManagementReview: 'Management review',
    fRefPrefixNonconformity:    'Nonconformity',
    fRefPrefixObjective:        'Objective',
    fRefPrefixDocument:         'Document',
    fRefPrefixMetric:           'Metric',
    fRefPrefixEvidence:         'Evidence',
    fRefPrefixLegalRegister:    'Legal register',
    // Section 2 — Certification
    fCertStatus:         'Certification status',
    certStatusOptions:   {
      none:         'Not certified',
      preparing:    'Preparing for certification',
      stage1:       'Stage 1 audit',
      stage2:       'Stage 2 audit',
      certified:    'Certified',
      surveillance: 'Surveillance audit',
      expired:      'Expired',
    },
    fCertBody:           'Certification body',
    fCertNumber:         'Certificate number',
    fStage1Date:         'Stage 1 audit date',
    fStage2Date:         'Stage 2 audit date',
    fCertDate:           'Certification date',
    fRecertDate:         'Recertification date',
    // Section 3 — Documents
    fDefaultClass:            'Default classification',
    fDefaultClassEmpty:       'Define classification levels first',
    fDefaultClassPlaceholder: 'Select…',
    fDocClass:           'Classification levels',
    addBtn:              'Add',
    addClassPlaceholder: 'New level…',
    // Section 4 — Technologies
    addTechPlaceholder:  'AWS, Microsoft 365, GitHub…',
    // Section — Jurisdictions
    fRegions:               'Jurisdictions',
    fRegionsHint:           'Only jurisdictions with direct regulatory obligations; not where only individual remote workers or customers without operational presence exist.',
    addRegionPlaceholder:   'Cataluña, Euskadi, California, Bayern…',
    // Section 5 — AI Context
    fAiProcessingConsent:     'AI processing consent',
    fAiProcessingConsentDate: 'Consent date',
    aiProcessingConsentHelper:
      'Allows AI to generate documents and suggestions for this ISMS. Disabling this turns off AI features.',
    fAiContext:          'AI context',
    fAiContextHelper:    'Text included in AI prompts to personalise generated documents. Include the ISMS scope and any relevant organization context here. Do not include people names, client names or any identifying data.',
    fAiContextPlaceholder: 'E.g. Tech company with 50-100 employees. ISMS covers SaaS platform development and operations. Key technologies: AWS, GitHub, Microsoft 365. Applicable compliance: GDPR, NIS2.',
    // Section — Awareness coverage policy
    fAwarenessPolicyEnable: 'Define an organization-wide coverage policy',
    fAwarenessTarget:       'Coverage target (%)',
    fAwarenessThresholds:   'RAG thresholds (%)',
    fAwarenessGreen:        'Green ≥',
    fAwarenessAmber:        'Amber ≥',
    fAwarenessRed:          'Red <',
    fAwarenessRedReadonly:  'Below amber',
    helpAwarenessPolicy:    'Defines how awareness program coverage is evaluated. Changes apply immediately to all programs, including past years — the numbers do not rewrite history, only how it is compared against today’s policy. Without a policy defined, the Awareness module behaves like today: plain percentages, no RAG, no alert.',
    // Section 6 — People
    addPerson:           'Add person',
    editPerson:          'Edit',
    deactivate:          'Deactivate',
    reactivate:          'Reactivate',
    savePerson:          'Save',
    cancelPerson:        'Cancel',
    fPersonName:         'Name',
    fPersonEmail:        'Email',
    fPersonRole:         'Role',
    fPersonDept:         'Department',
    fPersonCountry:      'Country',
    fPersonCity:         'City',
    fPersonTimezone:     'Time zone',
    emailRequired:       'Email is required.',
    emailDuplicate:      'This email is already registered for another person.',
    timezoneWarning:     'Unrecognized time zone format. Consider using IANA format (e.g. Europe/Madrid).',
    colName:             'Name',
    colRole:             'Role',
    colDept:             'Department',
    colCountry:          'Country',
    colStatus:           'Status',
    colActions:          'Actions',
    statusActive:        'Active',
    statusInactive:      'Inactive',
    noPeople:            'No people registered yet.',
    nameRequired:        'Name is required.',
    newPersonTitle:      'New person',
    editPersonTitle:     'Edit person',
    deletePerson:        'Delete',
    deletePersonConfirm: 'Are you sure you want to delete "{name}"? Make sure they are not assigned to any existing records before deleting.',
    deleteBtn:           'Delete',
    selectAll:           'Select all',
    nSelected:           '{n} selected',
    deleteSelected:      'Delete selected',
    deactivateSelected:  'Deactivate selected',
    reactivateSelected:  'Reactivate selected',
    deleteSelectedConfirm: 'Delete the following people?\n\n{names}\n\nMake sure they are not assigned to any existing records.',
    deactivateSelectedConfirm: 'Deactivate the following people?\n\n{names}\n\nThey will be marked as inactive. You can adjust their end date in each record.',
    reactivateSelectedConfirm: 'Reactivate the following people?\n\n{names}',
    importCsv:             'Import CSV',
    importPeopleTitle:     'Import people',
    formCancel:            'Cancel',
    fPersonType:           'Type',
    personTypeOptions:     { employee: 'Employee', contractor: 'Contractor', external: 'External' },
    // Section — Reopen records (§11 rule 35)
    reopenHeading:         'Reopen closed records',
    reopenHelper:          'Records in a terminal state (NC Closed, Incident Closed, Audit Completed, Management Review with approvalDate set, Document Retired/Superseded) are immutable. Use this section to return a record to an editable state. Each reopen is logged in the record\u2019s changeLog with the reason you provide.',
    reopenNcGroup:         'Closed nonconformities',
    reopenIncGroup:        'Closed incidents',
    reopenAudGroup:        'Completed audits',
    reopenMrGroup:         'Approved management reviews',
    reopenDocGroup:        'Retired/superseded documents',
    reopenEmptyGroup:      'No records in terminal state.',
    reopenLoading:         'Loading records…',
    reopenBtn:             'Reopen',
    reopenModalTitle:      'Reopen record',
    reopenModalHelper:     'Provide the reason for reopening. It will be recorded in the record\u2019s changeLog.',
    reopenReasonLabel:     'Reason',
    reopenReasonRequired:  'A reason is required.',
    reopenConfirmBtn:      'Confirm reopen',
    reopenCancelBtn:       'Cancel',
    reopenError:           'Could not reopen the record. Please try again.',
    fStartDate:            'Start date',
    fEndDate:              'End date',
    fIsResponsibilityRole: 'Responsibility role',
    // §11 rule 36 — person delete reference scan
    deletePersonBlockedMsg:   'Person "{name}" is referenced in existing records and cannot be deleted. Deactivate instead?',
    deleteSelectedBlockedMsg: '{blocked} of {total} selected people are referenced in existing records. To protect data integrity, none of the {total} will be deleted. Deactivate all {total} instead?',
    deactivateInstead:        'Deactivate',
    scanningPeople:           'Checking references…',
  },
};

// ── SIDEBAR SECTIONS ──────────────────────────────────────────────────────────
const SECTIONS = [
  { key: 'organization',  label: { es: 'Organización',     en: 'Organization'        } },
  { key: 'ismsRoles',     label: { es: 'Roles SGSI',       en: 'ISMS Roles'          } },
  { key: 'certification', label: { es: 'Certificación',    en: 'Certification'       } },
  { key: 'documents',     label: { es: 'Documentos',       en: 'Documents'           } },
  { key: 'awareness',     label: { es: 'Concienciación',   en: 'Awareness'           } },
  { key: 'refPrefixes',   label: { es: 'Prefijos',         en: 'Reference prefixes'  } },
  { key: 'technologies',  label: { es: 'Tecnologías',      en: 'Technologies'        } },
  { key: 'ai',            label: { es: 'Contexto IA',      en: 'AI Context'          } },
  { key: 'people',        label: { es: 'Personas',         en: 'People'              } },
  // §11 rule 35 — Reopen flow. Only entry point for returning a closed
  // record to an editable state; no per-record UI exists elsewhere.
  { key: 'reopen',        label: { es: 'Reabrir registros', en: 'Reopen records'     } },
];

// Entity type → folder mapping for the reopen flow. Matches the keys used
// in priorEditableStateDescriptor (utils/changeLog.js).
const REOPEN_FOLDER_BY_TYPE = {
  nonconformity:    'nonconformities',
  incident:         'incidents',
  audit:            'audits',
  managementReview: 'management-reviews',
  document:         'documents',
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function nextPersonId(arr) {
  const maxSeq = arr.reduce((max, p) => {
    const m = /^person-(\d+)$/.exec(p.id || '');
    return m ? Math.max(max, parseInt(m[1], 10)) : max;
  }, 0);
  return 'person-' + String(maxSeq + 1).padStart(3, '0');
}

// ── §11 rule 36 — PERSON REFERENCE SCAN ──────────────────────────────────────
// Returns the subset of personIds that have at least one reference anywhere in
// the ISMS data. Reads all entities in parallel; missing or corrupt files are
// skipped silently. Never throws.
async function scanPersonReferences(orgId, personIds) {
  const idSet = new Set(personIds);
  const referenced = new Set();

  const checkScalar = (val) => {
    if (val && idSet.has(val)) referenced.add(val);
  };
  const checkArray = (arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((v) => { if (v && idSet.has(v)) referenced.add(v); });
  };
  const checkChangeLogs = (entity) => {
    if (!Array.isArray(entity?.changeLog)) return;
    entity.changeLog.forEach((entry) => {
      if (entry?.changedBy && idSet.has(entry.changedBy)) referenced.add(entry.changedBy);
    });
  };

  await Promise.all([
    // ── profile.json ──────────────────────────────────────────────────────────
    (async () => {
      try {
        const profile = await readOrgFile(orgId, 'profile.json');
        checkScalar(profile?.consultantPersonId);
        checkArray(profile?.ismsRoles?.topManagementSignatory);
        checkScalar(profile?.ismsRoles?.ismsManager);
        checkScalar(profile?.ismsRoles?.ciso);
        checkScalar(profile?.ismsRoles?.dpo);
        checkScalar(profile?.ismsRoles?.nis2Contact);
        checkChangeLogs(profile);
      } catch { /* skip missing or corrupt file */ }
    })(),

    // ── context.json ──────────────────────────────────────────────────────────
    (async () => {
      try {
        const context = await readOrgFile(orgId, 'context.json');
        if (Array.isArray(context?.reviewHistory)) {
          context.reviewHistory.forEach((h) => {
            if (h?.reviewedBy && String(h.reviewedBy).startsWith('person-')) checkScalar(h.reviewedBy);
          });
        }
        checkChangeLogs(context);
      } catch { /* skip */ }
    })(),

    // ── scope.json ────────────────────────────────────────────────────────────
    (async () => {
      try {
        const scope = await readOrgFile(orgId, 'scope.json');
        checkArray(scope?.approvedBy);
        checkChangeLogs(scope);
      } catch { /* skip */ }
    })(),

    // ── controls/soa.json ─────────────────────────────────────────────────────
    (async () => {
      try {
        const soa = await readOrgFile(orgId, 'controls/soa.json');
        checkScalar(soa?.approvedBy);
        if (Array.isArray(soa?.controls)) {
          soa.controls.forEach((ctrl) => checkScalar(ctrl?.owner));
        }
        checkChangeLogs(soa);
      } catch { /* skip */ }
    })(),

    // ── rtp.json ──────────────────────────────────────────────────────────────
    (async () => {
      try {
        const rtp = await readOrgFile(orgId, 'rtp.json');
        checkScalar(rtp?.approvedBy);
        if (Array.isArray(rtp?.entries)) {
          rtp.entries.forEach((entry) => {
            checkScalar(entry?.owner);                   // §4 doc-debt: rtp.entries[].owner and rtp.entries[].residualRiskAcceptedBy not yet listed in §4 table
            checkScalar(entry?.residualRiskAcceptedBy);  // §4 doc-debt: rtp.entries[].owner and rtp.entries[].residualRiskAcceptedBy not yet listed in §4 table
          });
        }
        checkChangeLogs(rtp);
      } catch { /* skip */ }
    })(),

    // ── risks/ ────────────────────────────────────────────────────────────────
    (async () => {
      try {
        const risks = await listOrgCollection(orgId, 'risks');
        risks.forEach((r) => {
          checkScalar(r?.identifiedBy);
          checkScalar(r?.owner);
          checkArray(r?.treatmentActionOwner);
          checkScalar(r?.treatmentApprovedBy);
          checkScalar(r?.acceptedBy);
          checkChangeLogs(r);
        });
      } catch { /* skip */ }
    })(),

    // ── objectives/ ───────────────────────────────────────────────────────────
    (async () => {
      try {
        const objectives = await listOrgCollection(orgId, 'objectives');
        objectives.forEach((o) => {
          checkScalar(o?.owner);
          checkScalar(o?.approvedBy);
          checkArray(o?.responsiblePersons);
          checkChangeLogs(o);
        });
      } catch { /* skip */ }
    })(),

    // ── assets/ ───────────────────────────────────────────────────────────────
    (async () => {
      try {
        const assets = await listOrgCollection(orgId, 'assets');
        assets.forEach((a) => {
          checkScalar(a?.owner);
          checkScalar(a?.custodian);
          checkChangeLogs(a);
        });
      } catch { /* skip */ }
    })(),

    // ── incidents/ ────────────────────────────────────────────────────────────
    (async () => {
      try {
        const incidents = await listOrgCollection(orgId, 'incidents');
        incidents.forEach((inc) => {
          checkScalar(inc?.incidentCoordinator);
          if (inc?.reportedBy && String(inc.reportedBy).startsWith('person-')) checkScalar(inc.reportedBy);
          checkChangeLogs(inc);
        });
      } catch { /* skip */ }
    })(),

    // ── documents/ ────────────────────────────────────────────────────────────
    (async () => {
      try {
        const documents = await listOrgCollection(orgId, 'documents');
        documents.forEach((doc) => {
          checkScalar(doc?.owner);
          checkArray(doc?.approvedBy);
          if (Array.isArray(doc?.changeHistory)) {
            doc.changeHistory.forEach((h) => {
              if (h?.author && String(h.author).startsWith('person-')) checkScalar(h.author);
            });
          }
          if (Array.isArray(doc?.reviewHistory)) {
            doc.reviewHistory.forEach((h) => {
              if (h?.reviewedBy && String(h.reviewedBy).startsWith('person-')) checkScalar(h.reviewedBy);
            });
          }
          checkChangeLogs(doc);
        });
      } catch { /* skip */ }
    })(),

    // ── evidence/ ─────────────────────────────────────────────────────────────
    (async () => {
      try {
        const evidence = await listOrgCollection(orgId, 'evidence');
        evidence.forEach((ev) => {
          checkScalar(ev?.collectedBy);
          checkChangeLogs(ev);
        });
      } catch { /* skip */ }
    })(),

    // ── competence/ ───────────────────────────────────────────────────────────
    (async () => {
      try {
        const programs = await listOrgCollection(orgId, 'competence');
        programs.forEach((prog) => {
          if (Array.isArray(prog?.roles)) {
            prog.roles.forEach((role) => {
              checkArray(role?.personIds);
              if (Array.isArray(role?.trainingActions)) {
                role.trainingActions.forEach((action) => checkScalar(action?.responsible));
              }
            });
          }
          checkChangeLogs(prog);
        });
      } catch { /* skip */ }
    })(),

    // ── customers/ ────────────────────────────────────────────────────────────
    (async () => {
      try {
        const customers = await listOrgCollection(orgId, 'customers');
        customers.forEach((c) => {
          checkScalar(c?.technicalContact);
          checkScalar(c?.legalContact);
          checkScalar(c?.incidentNotificationContact);
          checkChangeLogs(c);
        });
      } catch { /* skip */ }
    })(),

    // ── metrics/ ──────────────────────────────────────────────────────────────
    (async () => {
      try {
        const metrics = await listOrgCollection(orgId, 'metrics');
        metrics.forEach((m) => {
          checkScalar(m?.responsible);
          checkChangeLogs(m);
        });
      } catch { /* skip */ }
    })(),

    // ── audits/ ───────────────────────────────────────────────────────────────
    (async () => {
      try {
        const audits = await listOrgCollection(orgId, 'audits');
        audits.forEach((aud) => {
          if (aud?.type === 'Internal' && aud?.auditor && String(aud.auditor).startsWith('person-')) {
            checkScalar(aud.auditor);
          }
          checkChangeLogs(aud);
        });
      } catch { /* skip */ }
    })(),

    // ── management-reviews/ ───────────────────────────────────────────────────
    (async () => {
      try {
        const mrs = await listOrgCollection(orgId, 'management-reviews');
        mrs.forEach((mr) => {
          checkScalar(mr?.chairperson);
          checkScalar(mr?.approvedBy);
          checkArray(mr?.attendees);
          if (Array.isArray(mr?.decisions)) {
            mr.decisions.forEach((d) => checkScalar(d?.ownerId));
          }
          if (Array.isArray(mr?.actions)) {
            mr.actions.forEach((a) => checkScalar(a?.responsible));
          }
          checkChangeLogs(mr);
        });
      } catch { /* skip */ }
    })(),

    // ── nonconformities/ ──────────────────────────────────────────────────────
    (async () => {
      try {
        const ncs = await listOrgCollection(orgId, 'nonconformities');
        ncs.forEach((nc) => {
          checkScalar(nc?.owner);
          checkScalar(nc?.verifiedBy);
          if (Array.isArray(nc?.corrections)) {
            nc.corrections.forEach((c) => checkScalar(c?.responsible));
          }
          if (Array.isArray(nc?.correctiveActions)) {
            nc.correctiveActions.forEach((ca) => checkArray(ca?.responsible));
          }
          checkChangeLogs(nc);
        });
      } catch { /* skip */ }
    })(),

    // ── legal-register/ ───────────────────────────────────────────────────────
    (async () => {
      try {
        const lrs = await listOrgCollection(orgId, 'legal-register');
        lrs.forEach((lr) => {
          checkScalar(lr?.responsiblePerson);
          checkChangeLogs(lr);
        });
      } catch { /* skip */ }
    })(),

    // ── people.json (reportsTo) ───────────────────────────────────────────────
    (async () => {
      try {
        const data = await readOrgFile(orgId, 'people.json');
        if (Array.isArray(data?.people)) {
          data.people.forEach((p) => {
            if (p?.reportsTo) checkScalar(p.reportsTo); // §4 doc-debt: people[].reportsTo not yet listed in §4 table
          });
        }
      } catch { /* skip */ }
    })(),
  ]);

  return referenced;
}

// ── CHIP ──────────────────────────────────────────────────────────────────────
function Chip({ label, onRemove, canRemove }) {
  return (
    <span style={{
      ...FONT_LABEL,
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      background: '#f0fdf4', border: '1px solid #bbf7d0', color: BRAND_PRIMARY,
      borderRadius: 20, padding: '0.2rem 0.55rem 0.2rem 0.7rem',
    }}>
      {label}
      {canRemove && (
        <button
          onClick={onRemove}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: BRAND_PRIMARY, padding: 0, fontSize: '1rem',
            lineHeight: 1, display: 'flex', alignItems: 'center',
          }}
        >×</button>
      )}
    </span>
  );
}

// ── FREE-TEXT SANITISER ──────────────────────────────────────────────────────
// Strips the organisation name, the responsible consultant name, and email-like
// patterns from free-text profile fields before they are written to disk and
// later sent to the Worker. Substitutions are case-insensitive and require a
// minimum length of 3 for org and consultant names to avoid spurious matches.
function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function sanitiseProfileFreeText(input, orgName, consultantName) {
  if (input == null || input === '') return input;
  if (typeof input !== 'string') return input;
  let out = input;
  if (typeof orgName === 'string' && orgName.length >= 3) {
    try {
      out = out.replace(new RegExp(escapeRegex(orgName), 'gi'), '[org]');
    } catch { /* noop */ }
  }
  if (typeof consultantName === 'string' && consultantName.length >= 3) {
    try {
      out = out.replace(new RegExp(escapeRegex(consultantName), 'gi'), '[consultant]');
    } catch { /* noop */ }
  }
  out = out.replace(/[\w.+_-]+@[\w.-]+\.[A-Za-z]{2,}/g, '[email]');
  return out;
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function SettingsView({ org, lang, initialSection, onBack, onOrgUpdated, onDirtyChange, saveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  // ── Profile state ─────────────────────────────────────────────────────────
  const [profile,      setProfile]     = useState(null);
  const [dirty,        setDirty]       = useState(false);
  const [saveState,    setSaveState]   = useState('idle');
  const [newClassTag,       setNewClassTag]       = useState('');
  const [newTechTag,        setNewTechTag]        = useState('');
  const [newRegionTag,      setNewRegionTag]      = useState('');

  // ── Layout state ──────────────────────────────────────────────────────────
  const [activeSection, setActiveSection] = useState(initialSection || 'organization');
  const [hoveredNav,    setHoveredNav]    = useState(null);

  // ── People state ──────────────────────────────────────────────────────────
  const [people,          setPeople]          = useState([]);
  const [peopleDirty,     setPeopleDirty]     = useState(false);
  const [peopleSaveState, setPeopleSaveState] = useState('idle');
  const [peopleView,      setPeopleView]      = useState('list');
  const [editingPerson,   setEditingPerson]   = useState(null);
  const [personForm,      setPersonForm]      = useState({
    name: '', email: '', role: '', department: '', country: '',
    city: '', timezone: '',
    type: '', startDate: '', endDate: '', isResponsibilityRole: false,
  });
  const [personNameErr, setPersonNameErr] = useState(false);
  const [emailErr,      setEmailErr]      = useState(null);   // null | 'required' | 'duplicate'
  const [emailTouched,  setEmailTouched]  = useState(false);
  const [timezoneWarn,  setTimezoneWarn]  = useState(false);
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [showPeopleImport, setShowPeopleImport] = useState(false);
  const [confirmState, setConfirmState]   = useState(null);
  const [scanningDelete, setScanningDelete] = useState(false);

  // ── §11 rule 35 — Reopen flow state ───────────────────────────────────────
  // closedRecords is loaded lazily when the user first opens the reopen
  // section (keeps the six FS reads off the mount path).
  // reopenModal holds the chosen record + entity type + the free-text reason
  // the user is typing in the confirmation dialog.
  const [closedRecords, setClosedRecords] = useState(null); // null = not loaded
  const [reopenModal, setReopenModal]     = useState(null); // { record, entityType, reason, error, saving }

  // ── Load profile ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    readOrgFile(org.id, 'profile.json')
      .then((p) => {
        if (cancelled) return;
        // Backwards-compatible migration: ismsRoles.topManagementSignatory
        // legacy values were string | null; the field is now string[].
        const roles = p?.ismsRoles || {};
        const tms = roles.topManagementSignatory;
        let migrated;
        if (Array.isArray(tms)) {
          migrated = tms;
        } else if (typeof tms === 'string' && tms !== '') {
          migrated = [tms];
        } else {
          migrated = [];
        }
        // Backwards-compatible migration: country legacy values were
        // string | null; the field is now string[].
        const c = p?.country;
        let migratedCountry;
        if (Array.isArray(c)) {
          migratedCountry = c;
        } else if (typeof c === 'string' && c !== '') {
          migratedCountry = [c];
        } else {
          migratedCountry = [];
        }
        // Backwards-compatible migration: aiContext merges legacy
        // scopeSummary, scope and additionalContext into a single field.
        let migratedAiContext;
        if (typeof p?.aiContext === 'string') {
          migratedAiContext = p.aiContext;
        } else {
          const parts = [p?.scopeSummary, p?.scope, p?.additionalContext]
            .map(v => (typeof v === 'string' ? v.trim() : ''))
            .filter(v => v !== '');
          const deduped = [];
          parts.forEach((v, i) => {
            if (i > 0 && v === parts[i - 1]) return;
            deduped.push(v);
          });
          migratedAiContext = deduped.join('\n\n');
        }
        // Backwards-compatible migration: organizationType defaults to 'private'
        // when absent or invalid; this matches the canonical assumption that
        // legacy profiles without explicit declaration are private organisations.
        const migratedOrganizationType =
          (typeof p?.organizationType === 'string' && ORGANIZATION_TYPES.includes(p.organizationType))
            ? p.organizationType
            : 'private';
        // Backwards-compatible migration: publicSectorExposure defaults to 'none'
        // when absent or invalid; legacy private profiles are assumed to have no
        // public-sector exposure.
        const migratedPublicSectorExposure =
          (typeof p?.publicSectorExposure === 'string' && PUBLIC_SECTOR_EXPOSURE_VALUES.includes(p.publicSectorExposure))
            ? p.publicSectorExposure
            : 'none';
        // Backwards-compatible migration: publicSectorExposureDescription
        // defaults to '' when absent.
        const migratedPublicSectorExposureDescription =
          typeof p?.publicSectorExposureDescription === 'string'
            ? p.publicSectorExposureDescription
            : '';
        setProfile({
          ...p,
          ismsRoles: { ...roles, topManagementSignatory: migrated },
          country: migratedCountry,
          aiContext: migratedAiContext,
          organizationType: migratedOrganizationType,
          publicSectorExposure: migratedPublicSectorExposure,
          publicSectorExposureDescription: migratedPublicSectorExposureDescription,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [org.id]);

  // ── Load people ───────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    readOrgFile(org.id, 'people.json')
      .then((data) => {
        if (!cancelled) setPeople(Array.isArray(data.people) ? data.people : []);
      })
      .catch(() => { if (!cancelled) setPeople([]); });
    return () => { cancelled = true; };
  }, [org.id]);

  // ── §11 rule 35 — lazy-load closed records on first visit to section ─────
  useEffect(() => {
    if (activeSection !== 'reopen') return;
    if (closedRecords !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const [ncs, incidents, audits, mrs, docs] = await Promise.all([
          listOrgCollection(org.id, 'nonconformities'),
          listOrgCollection(org.id, 'incidents'),
          listOrgCollection(org.id, 'audits'),
          listOrgCollection(org.id, 'management-reviews'),
          listOrgCollection(org.id, 'documents'),
        ]);
        if (cancelled) return;
        setClosedRecords({
          nonconformity:    (ncs || []).filter(r => r && r.status === 'Closed'),
          incident:         (incidents || []).filter(r => r && r.status === 'Closed'),
          audit:            (audits || []).filter(r => r && r.status === 'Completed'),
          managementReview: (mrs || []).filter(r => r && !!r.approvalDate),
          document:         (docs || []).filter(r => r && (r.status === 'Superseded' || r.status === 'Retired')),
        });
      } catch {
        if (!cancelled) {
          setClosedRecords({ nonconformity: [], incident: [], audit: [], managementReview: [], document: [] });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeSection, org.id, closedRecords]);

  const handleReopenConfirm = async () => {
    if (!reopenModal) return;
    const reason = (reopenModal.reason || '').trim();
    if (!reason) {
      setReopenModal(prev => prev ? { ...prev, error: 'reason-required' } : prev);
      return;
    }
    const { record, entityType } = reopenModal;
    const descriptor = priorEditableStateDescriptor(entityType, record);
    if (!descriptor) return;
    setReopenModal(prev => prev ? { ...prev, saving: true, error: null } : prev);
    try {
      const now = new Date().toISOString();
      const changedBy = (profile && profile.consultantPersonId) || 'system';
      const updated = {
        ...record,
        ...descriptor.patch,
        updatedAt: now,
        changeLog: [
          ...(Array.isArray(record.changeLog) ? record.changeLog : []),
          {
            timestamp: now,
            field: descriptor.field,
            previousValue: descriptor.previousValue,
            newValue: descriptor.newValue,
            changedBy,
            reason,
          },
        ],
      };
      const folder = REOPEN_FOLDER_BY_TYPE[entityType];
      await writeOrgFile(org.id, `${folder}/${record.id}.json`, updated);
      // Remove the record from the local list now that it is editable again.
      setClosedRecords(prev => prev ? {
        ...prev,
        [entityType]: prev[entityType].filter(r => r.id !== record.id),
      } : prev);
      setReopenModal(null);
    } catch {
      setReopenModal(prev => prev ? { ...prev, saving: false, error: 'save-failed' } : prev);
    }
  };

  // ── beforeunload guard ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dirty || peopleDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, peopleDirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    const isDirty = dirty || peopleDirty;
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [dirty, peopleDirty, onDirtyChange]);

  // ── Profile helpers ───────────────────────────────────────────────────────
  const set = (field, value) => {
    setDirty(true);
    setProfile((prev) => prev ? { ...prev, [field]: value } : prev);
  };

  const setNested = (parent, field, value) => {
    setDirty(true);
    setProfile((prev) =>
      prev ? { ...prev, [parent]: { ...(prev[parent] || {}), [field]: value } } : prev
    );
  };

  // ── Save (profile + optionally people) ───────────────────────────────────
  // doSave returns true on a successful write of every dirty surface, false
  // on any save error. Used both by handleSave (no return value consumed)
  // and by the App-level guard via saveRequestTick / onSaveResult.
  const doSave = async () => {
    if (!profile || saveState === 'saving') return false;
    let allOk = true;
    if (dirty) {
      setSaveState('saving');
      try {
        if (profile.certificationStatus == null) {
          setProfile((prev) => prev ? { ...prev, certificationStatus: 'none' } : prev);
        }
        const consultantPerson = (profile.consultantPersonId && Array.isArray(people))
          ? people.find((p) => p && p.id === profile.consultantPersonId)
          : null;
        const consultantNameForSanitiser = consultantPerson ? (consultantPerson.name || null) : null;
        const sanitisedAiContext = sanitiseProfileFreeText(profile.aiContext, profile.name, consultantNameForSanitiser);
        const sanitisedPublicSectorExposureDescription = sanitiseProfileFreeText(profile.publicSectorExposureDescription, profile.name, consultantNameForSanitiser);
        const updated = {
          ...profile,
          certificationStatus:  profile.certificationStatus ?? 'none',
          norms:                (profile.norms && profile.norms.length > 0) ? profile.norms : [SUPPORTED_NORMS[0]],
          pdcaStage:            profile.pdcaStage || 'Plan',
          mrFrequencyMonths:    Number(profile.mrFrequencyMonths) || 12,
          auditFrequencyMonths: Number(profile.auditFrequencyMonths) || 12,
          consultantPersonId:   profile.consultantPersonId || null,
          riskMatrix: {
            ...(profile.riskMatrix || {}),
            thresholds: {
              low:    Number(profile.riskMatrix?.thresholds?.low)    || 4,
              medium: Number(profile.riskMatrix?.thresholds?.medium) || 9,
            },
          },
          ismsRoles: {
            ...(profile.ismsRoles || {}),
            topManagementSignatory: Array.isArray(profile.ismsRoles?.topManagementSignatory)
              ? profile.ismsRoles.topManagementSignatory
              : (typeof profile.ismsRoles?.topManagementSignatory === 'string' && profile.ismsRoles.topManagementSignatory !== ''
                  ? [profile.ismsRoles.topManagementSignatory]
                  : []),
            ...(profile.cisoSameAsIsmsManager ? { ciso: profile.ismsRoles?.ismsManager ?? null } : {}),
          },
          aiContext: sanitisedAiContext,
          publicSectorExposureDescription: sanitisedPublicSectorExposureDescription,
          updatedAt: new Date().toISOString(),
        };
        await writeOrgFile(org.id, 'profile.json', updated);
        setProfile(updated);
        setDirty(false);
        setSaveState('ok');
        onOrgUpdated(updated);
        // §4 pdcaStage auto-advance — Settings is the one place where the
        // Act → Plan cycle trigger (certificationStatus = "surveillance") is
        // typically recorded. Run after the profile write so the follow-up
        // stage-advance write sees the just-saved value.
        maybeAdvancePdcaStage(org.id, updated, (advanced) => {
          setProfile(advanced);
          onOrgUpdated(advanced);
        }).catch(() => {});
        setTimeout(() => setSaveState('idle'), 2000);
      } catch {
        setSaveState('error');
        setTimeout(() => setSaveState('idle'), 3000);
        allOk = false;
      }
    }
    if (peopleDirty) {
      setPeopleSaveState('saving');
      try {
        await writeOrgFile(org.id, 'people.json', {
          orgId: org.id, people, updatedAt: new Date().toISOString(),
        });
        setPeopleDirty(false);
        setPeopleSaveState('ok');
        setTimeout(() => setPeopleSaveState('idle'), 2000);
      } catch {
        setPeopleSaveState('error');
        setTimeout(() => setPeopleSaveState('idle'), 3000);
        allOk = false;
      }
    }
    return allOk;
  };

  const handleSave = async () => {
    if (!profile || saveState === 'saving') return;
    setSaveState('saving');
    try {
      if (profile.certificationStatus == null) {
        setProfile((prev) => prev ? { ...prev, certificationStatus: 'none' } : prev);
      }
      const updated = {
        ...profile,
        certificationStatus:  profile.certificationStatus ?? 'none',
        norms:                (profile.norms && profile.norms.length > 0) ? profile.norms : [SUPPORTED_NORMS[0]],
        pdcaStage:            profile.pdcaStage || 'Plan',
        mrFrequencyMonths:    Number(profile.mrFrequencyMonths) || 12,
        auditFrequencyMonths: Number(profile.auditFrequencyMonths) || 12,
        consultantPersonId:   profile.consultantPersonId || null,
        riskMatrix: {
          ...(profile.riskMatrix || {}),
          thresholds: {
            low:    Number(profile.riskMatrix?.thresholds?.low)    || 4,
            medium: Number(profile.riskMatrix?.thresholds?.medium) || 9,
          },
        },
        ismsRoles: {
          ...(profile.ismsRoles || {}),
          topManagementSignatory: Array.isArray(profile.ismsRoles?.topManagementSignatory)
            ? profile.ismsRoles.topManagementSignatory
            : (typeof profile.ismsRoles?.topManagementSignatory === 'string' && profile.ismsRoles.topManagementSignatory !== ''
                ? [profile.ismsRoles.topManagementSignatory]
                : []),
          ...(profile.cisoSameAsIsmsManager ? { ciso: profile.ismsRoles?.ismsManager ?? null } : {}),
        },
        updatedAt: new Date().toISOString(),
      };
      await writeOrgFile(org.id, 'profile.json', updated);
      setProfile(updated);
      setDirty(false);
      setSaveState('ok');
      onOrgUpdated(updated);
      // §4 pdcaStage auto-advance — Settings is the one place where the
      // Act → Plan cycle trigger (certificationStatus = "surveillance") is
      // typically recorded. Run after the profile write so the follow-up
      // stage-advance write sees the just-saved value.
      maybeAdvancePdcaStage(org.id, updated, (advanced) => {
        setProfile(advanced);
        onOrgUpdated(advanced);
      }).catch(() => {});
      setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  // ── App-level guard: save on tick increase, report result back ───────────
  const lastProcessedTickRef = useRef(0);
  useEffect(() => {
    if (typeof saveRequestTick !== 'number') return;
    if (saveRequestTick <= lastProcessedTickRef.current) return;
    lastProcessedTickRef.current = saveRequestTick;
    (async () => {
      const ok = await doSave();
      onSaveResult?.(ok);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  // ── Tag helpers ───────────────────────────────────────────────────────────
  const addClassTag = () => {
    const val = newClassTag.trim();
    if (!val || (profile.documentClassification || []).includes(val)) return;
    set('documentClassification', [...(profile.documentClassification || []), val]);
    setNewClassTag('');
  };

  const removeClassTag = (tag) => {
    if (tag === profile.defaultClassification) return;
    set('documentClassification', (profile.documentClassification || []).filter((c) => c !== tag));
  };

  const addTechTag = () => {
    const val = newTechTag.trim();
    if (!val || (profile.technologies || []).includes(val)) return;
    set('technologies', [...(profile.technologies || []), val]);
    setNewTechTag('');
  };

  const removeTechTag = (tag) => {
    set('technologies', (profile.technologies || []).filter((c) => c !== tag));
  };

  // ── Regions (Jurisdictions) helpers ─────────────────────────────────────
  // Flattened catalogue of every ISO 3166-2 entry across all countries in
  // SUBREGIONS. Suggestions span all countries, not just `profile.country`,
  // because the regions field may legitimately span jurisdictions.
  const flatSubregions = Object.entries(SUBREGIONS).flatMap(([cc, list]) =>
    list.map((sr) => ({ ...sr, country: cc }))
  );
  const findCatalogueEntry = (val) => {
    for (const [, list] of Object.entries(SUBREGIONS)) {
      const found = list.find((sr) => sr.code === val);
      if (found) return found;
    }
    return null;
  };
  const regionChipLabel = (val) => {
    const found = findCatalogueEntry(val);
    if (!found) return val;
    const name = lang === 'es' ? found.nameEs : found.nameEn;
    return `${val} · ${name}`;
  };
  const addRegionTag = () => {
    const raw = newRegionTag.trim();
    if (!raw) return;
    // If the typed string matches a catalogue code (case-insensitive), normalise
    // to the canonical code. Otherwise accept verbatim as free-text.
    const match = flatSubregions.find((sr) => sr.code.toLowerCase() === raw.toLowerCase());
    const value = match ? match.code : raw;
    if ((profile.regions || []).includes(value)) { setNewRegionTag(''); return; }
    set('regions', [...(profile.regions || []), value]);
    setNewRegionTag('');
  };
  const removeRegionTag = (tag) => {
    set('regions', (profile.regions || []).filter((c) => c !== tag));
  };

  const toggleAiConsent = (checked) => {
    setDirty(true);
    setProfile((prev) => {
      if (!prev) return prev;
      if (checked) {
        return { ...prev, aiProcessingConsent: true, aiProcessingConsentDate: new Date().toISOString() };
      }
      return { ...prev, aiProcessingConsent: false };
    });
  };

  // ── People helpers ────────────────────────────────────────────────────────
  const handleSavePeople = async () => {
    if (peopleSaveState === 'saving') return;
    setPeopleSaveState('saving');
    try {
      await writeOrgFile(org.id, 'people.json', {
        orgId: org.id, people, updatedAt: new Date().toISOString(),
      });
      setPeopleDirty(false);
      setPeopleSaveState('ok');
      setTimeout(() => setPeopleSaveState('idle'), 2000);
    } catch {
      setPeopleSaveState('error');
      setTimeout(() => setPeopleSaveState('idle'), 3000);
    }
  };

  const openAddForm = () => {
    setEditingPerson(null);
    setPersonForm({
      name: '', email: '', role: '', department: '', country: '',
      city: '', timezone: '',
      type: '', startDate: '', endDate: '', isResponsibilityRole: false,
    });
    setPersonNameErr(false);
    setEmailErr(null);
    setEmailTouched(false);
    setTimezoneWarn(false);
    setPeopleView('form');
  };

  const openEditForm = (person) => {
    setEditingPerson(person);
    setPersonForm({
      name:                 person.name       || '',
      email:                person.email      || '',
      role:                 person.role       || '',
      department:           person.department || '',
      country:              person.country    || '',
      city:                 person.city       || '',
      timezone:             person.timezone   || '',
      type:                 person.type       || '',
      startDate:            person.startDate  || '',
      endDate:              person.endDate    || '',
      isResponsibilityRole: !!person.isResponsibilityRole,
    });
    setPersonNameErr(false);
    setEmailErr(null);
    setEmailTouched(false);
    setTimezoneWarn(false);
    setPeopleView('form');
  };

  const cancelPersonForm = () => {
    setPeopleView('list');
    setEditingPerson(null);
    setPersonNameErr(false);
    setEmailErr(null);
    setEmailTouched(false);
    setTimezoneWarn(false);
  };

  const savePersonForm = () => {
    if (!personForm.name.trim()) { setPersonNameErr(true); return; }

    // Email validation (§11 rule 41)
    const emailTrimmed = personForm.email.trim();
    if (!editingPerson && emailTrimmed === '') {
      setEmailErr('required');
      return;
    }
    if (editingPerson && emailTouched && emailTrimmed === '') {
      setEmailErr('required');
      return;
    }
    if (emailTrimmed !== '') {
      const lower = emailTrimmed.toLowerCase();
      const collision = people.find((p) =>
        p.email != null
        && p.email.toLowerCase() === lower
        && p.id !== editingPerson?.id
      );
      if (collision) {
        setEmailErr('duplicate');
        return;
      }
    }
    setEmailErr(null);

    // Timezone IANA warning (non-blocking)
    const tzTrimmed = personForm.timezone.trim();
    let tzList = [];
    try { tzList = Intl.supportedValuesOf('timeZone'); } catch { tzList = []; }
    if (tzTrimmed !== '' && !tzList.includes(tzTrimmed)) {
      setTimezoneWarn(true);
    } else {
      setTimezoneWarn(false);
    }

    if (editingPerson) {
      setPeople((prev) => prev.map((p) =>
        p.id === editingPerson.id
          ? {
              ...p,
              name:                 personForm.name.trim(),
              email:                emailTrimmed                 || null,
              role:                 personForm.role.trim()       || null,
              department:           personForm.department.trim() || null,
              country:              personForm.country.trim()    || null,
              city:                 personForm.city.trim()       || null,
              timezone:             personForm.timezone.trim()   || null,
              type:                 personForm.type              || null,
              startDate:            personForm.startDate         || null,
              endDate:              personForm.endDate           || null,
              isResponsibilityRole: !!personForm.isResponsibilityRole,
            }
          : p
      ));
    } else {
      setPeople((prev) => [...prev, {
        id:                   nextPersonId(people),
        name:                 personForm.name.trim(),
        email:                emailTrimmed                 || null,
        role:                 personForm.role.trim()       || null,
        department:           personForm.department.trim() || null,
        country:              personForm.country.trim()    || null,
        city:                 personForm.city.trim()       || null,
        timezone:             personForm.timezone.trim()   || null,
        type:                 personForm.type              || null,
        startDate:            personForm.startDate         || null,
        endDate:              personForm.endDate           || null,
        isResponsibilityRole: !!personForm.isResponsibilityRole,
        active:               true,
      }]);
    }
    setPeopleDirty(true);
    setPeopleView('list');
    setEditingPerson(null);
  };

  const togglePersonActive = (person) => {
    setPeople((prev) => prev.map((p) => p.id === person.id ? { ...p, active: !p.active } : p));
    setPeopleDirty(true);
  };

  const handleDeactivatePerson = (person) => {
    const today = new Date().toISOString().slice(0, 10);
    setPeople((prev) => prev.map((p) =>
      p.id === person.id ? { ...p, active: false, endDate: p.endDate || today } : p
    ));
    setPeopleDirty(true);
  };

  const handleDeletePerson = async (person) => {
    setScanningDelete(true);
    let referenced;
    try {
      referenced = await scanPersonReferences(org.id, [person.id]);
    } catch {
      setScanningDelete(false);
      return;
    }
    setScanningDelete(false);
    if (referenced.has(person.id)) {
      setConfirmState({
        message:      t.deletePersonBlockedMsg.replace('{name}', person.name),
        confirmLabel: t.deactivateInstead,
        confirmColor: BTN_SECONDARY,
        onConfirm: () => { setConfirmState(null); handleDeactivatePerson(person); },
      });
    } else {
      setConfirmState({
        message:      t.deletePersonConfirm.replace('{name}', person.name),
        confirmLabel: t.deleteBtn,
        onConfirm: () => {
          setConfirmState(null);
          setPeople((prev) => prev.filter((p) => p.id !== person.id));
          setSelectedIds((prev) => { const next = new Set(prev); next.delete(person.id); return next; });
          setPeopleDirty(true);
        },
      });
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    setScanningDelete(true);
    let referenced;
    try {
      referenced = await scanPersonReferences(org.id, [...selectedIds]);
    } catch {
      setScanningDelete(false);
      return;
    }
    setScanningDelete(false);
    const blockedCount = [...selectedIds].filter((id) => referenced.has(id)).length;
    if (blockedCount > 0) {
      setConfirmState({
        message:      t.deleteSelectedBlockedMsg
          .replace('{blocked}', String(blockedCount))
          .replace(/\{total\}/g, String(selectedIds.size)),
        confirmLabel: t.deactivateInstead,
        confirmColor: BTN_SECONDARY,
        onConfirm: () => {
          setConfirmState(null);
          const today = new Date().toISOString().slice(0, 10);
          setPeople((prev) => prev.map((p) =>
            selectedIds.has(p.id) ? { ...p, active: false, endDate: p.endDate || today } : p
          ));
          setSelectedIds(new Set());
          setPeopleDirty(true);
        },
      });
    } else {
      const toDelete = people.filter((p) => selectedIds.has(p.id));
      const names = toDelete.map((p) => p.name).join(', ');
      setConfirmState({
        message:      t.deleteSelectedConfirm.replace('{names}', names),
        confirmLabel: t.deleteBtn,
        onConfirm: () => {
          setConfirmState(null);
          setPeople((prev) => prev.filter((p) => !selectedIds.has(p.id)));
          setSelectedIds(new Set());
          setPeopleDirty(true);
        },
      });
    }
  };

  const handleDeactivateSelected = () => {
    const toUpdate = people.filter((p) => selectedIds.has(p.id));
    if (!toUpdate.length) return;
    const names = toUpdate.map((p) => p.name).join(', ');
    setConfirmState({
      message:      t.deactivateSelectedConfirm.replace('{names}', names),
      confirmLabel: t.deactivate,
      onConfirm: () => {
        setConfirmState(null);
        const today = new Date().toISOString().slice(0, 10);
        setPeople((prev) => prev.map((p) =>
          selectedIds.has(p.id)
            ? { ...p, active: false, endDate: p.endDate || today }
            : p
        ));
        setSelectedIds(new Set());
        setPeopleDirty(true);
      },
    });
  };

  const handleReactivateSelected = () => {
    const toUpdate = people.filter((p) => selectedIds.has(p.id));
    if (!toUpdate.length) return;
    const names = toUpdate.map((p) => p.name).join(', ');
    setConfirmState({
      message:      t.reactivateSelectedConfirm.replace('{names}', names),
      confirmLabel: t.reactivate,
      onConfirm: () => {
        setConfirmState(null);
        setPeople((prev) => prev.map((p) =>
          selectedIds.has(p.id)
            ? { ...p, active: true, endDate: null }
            : p
        ));
        setSelectedIds(new Set());
        setPeopleDirty(true);
      },
    });
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (!profile) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'row',
        minHeight: 'calc(100vh - 52px)', background: BG_SHELL,
      }}>
        <div style={{
          width: 200, flexShrink: 0,
          background: BG_CONTENT, borderRight: `1px solid ${BORDER_DEFAULT}`,
        }} />
        <div style={{
          ...FONT_LABEL,
          flex: 1, padding: '2rem 2.5rem',
          color: TEXT_MUTED,
        }}>
          {t.loading}
        </div>
      </div>
    );
  }

  // ── Shared styles ─────────────────────────────────────────────────────────
  const inputStyle = {
    ...FONT_BODY,
    width: '100%',
    border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6, padding: '0.45rem 0.65rem',
    background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none', boxSizing: 'border-box',
  };

  const fieldLabelStyle = {
    ...FONT_TINY,
    color: TEXT_DISABLED,
    letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block',
    marginBottom: '0.35rem',
  };

  const fieldStyle = { marginBottom: '1rem' };

  // Profile save button labels / colours
  const saveLabel = { idle: t.save, saving: t.saving, ok: t.saveOk, error: t.save }[saveState];
  const saveBg    = { idle: BTN_PRIMARY.background, saving: TEXT_MUTED, ok: BTN_PRIMARY.background, error: BTN_PRIMARY.background }[saveState];

  // People save button labels / colours
  const peopleSaveLabel = { idle: t.savePeople, saving: t.saving, ok: t.saveOk, error: t.savePeople }[peopleSaveState];
  const peopleSaveBg    = { idle: BTN_PRIMARY.background, saving: TEXT_MUTED, ok: BTN_PRIMARY.background, error: BTN_PRIMARY.background }[peopleSaveState];

  const isSaving = saveState === 'saving' || peopleSaveState === 'saving';

  const errorBannerSt = {
    ...FONT_LABEL,
    background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`,
    borderRadius: 6, padding: '0.35rem 0.75rem',
    marginBottom: '1rem',
  };

  const btnPrimary = {
    ...BTN_PRIMARY,
    ...FONT_BUTTON,
    border: 'none',
    borderRadius: 6, padding: '0.45rem 0.9rem', cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const btnSmall = {
    ...BTN_SECONDARY,
    ...FONT_TINY,
    borderRadius: 5,
    padding: '0.2rem 0.55rem', cursor: 'pointer',
  };

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex', flexDirection: 'row',
      minHeight: 'calc(100vh - 52px)', background: BG_SHELL,
    }}>

      {/* ── LEFT SIDEBAR ─────────────────────────────────────────────────── */}
      <div style={{
        width: 200, flexShrink: 0,
        background: BG_CONTENT, borderRight: `1px solid ${BORDER_DEFAULT}`,
      }}>
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            onMouseEnter={() => setHoveredNav(s.key)}
            onMouseLeave={() => setHoveredNav(null)}
            style={{
              ...FONT_SMALL,
              display: 'block', width: '100%', textAlign: 'left', border: 'none',
              cursor: 'pointer',
              padding: '0.55rem 1rem', transition: 'background 0.1s',
              background: activeSection === s.key
                ? TEXT_HEADING
                : hoveredNav === s.key ? BG_ACTIVE : 'transparent',
              color: activeSection === s.key ? SURFACE_WHITE : TEXT_DEFAULT,
            }}
          >
            {s.label[lang] || s.label.en}
          </button>
        ))}
      </div>

      {/* ── RIGHT CONTENT AREA ───────────────────────────────────────────── */}
      <div style={{ flex: 1, padding: '2rem 2.5rem', overflowY: 'auto' }}>

        {/* Header row: title left, save button right */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          marginBottom: '2rem', flexWrap: 'wrap', gap: '0.75rem',
        }}>
          <div>
            <h1 style={{
              ...FONT_PAGE_TITLE,
              color: TEXT_HEADING, margin: 0,
            }}>
              {t.title}
            </h1>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', alignSelf: 'flex-start' }}>
            {activeSection !== 'people' && activeSection !== 'reopen' ? (
              <>
                {dirty && (
                  <span style={{
                    ...FONT_LABEL, color: UNSAVED_AMBER,
                  }}>
                    {t.unsavedLabel}
                  </span>
                )}
                <button
                  onClick={handleSave}
                  disabled={saveState === 'saving'}
                  style={{
                    ...FONT_BUTTON,
                    background: saveBg, color: SURFACE_WHITE, border: 'none', borderRadius: 8,
                    padding: '0.55rem 1.25rem',
                    cursor: saveState === 'saving' ? 'default' : 'pointer',
                    transition: 'background 0.2s', whiteSpace: 'nowrap',
                  }}
                >
                  {saveLabel}
                </button>
              </>
            ) : peopleDirty && activeSection === 'people' && (
              <button
                onClick={handleSavePeople}
                disabled={peopleSaveState === 'saving'}
                style={{
                  ...FONT_BUTTON,
                  background: peopleSaveBg, color: SURFACE_WHITE, border: 'none', borderRadius: 8,
                  padding: '0.55rem 1.25rem',
                  cursor: peopleSaveState === 'saving' ? 'default' : 'pointer',
                  transition: 'background 0.2s', whiteSpace: 'nowrap',
                }}
              >
                {peopleSaveLabel}
              </button>
            )}
          </div>
        </div>

        {(saveState === 'error' || peopleSaveState === 'error') && (
          <div style={errorBannerSt}>{t.saveError}</div>
        )}

        <fieldset disabled={isSaving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

        {/* ── Section: Organization ──────────────────────────────────────── */}
        {activeSection === 'organization' && (
          <>
            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fName}</span>
              <input
                value={profile.name || ''}
                onChange={(e) => set('name', e.target.value)}
                style={inputStyle}
              />
            </div>

            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fConsultantPerson}</span>
              <PeopleSelector
                orgId={org.id}
                lang={lang}
                value={profile.consultantPersonId || null}
                onChange={(v) => set('consultantPersonId', v || null)}
              />
              <p style={{
                ...FONT_SMALL,
                color: TEXT_DISABLED, margin: '0.35rem 0 0', lineHeight: 1.5,
              }}>
                {t.fConsultantPersonHelper}
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={fieldStyle}>
                <span style={fieldLabelStyle}>{t.fSector}</span>
                <input
                  value={profile.sector || ''}
                  onChange={(e) => set('sector', e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div style={fieldStyle}>
                <span style={fieldLabelStyle}>{t.fSize}</span>
                <input
                  type="number"
                  value={profile.size || ''}
                  onChange={(e) => set('size', e.target.value ? Number(e.target.value) : '')}
                  style={inputStyle}
                />
              </div>
              <div style={fieldStyle}>
                <span style={fieldLabelStyle}>{t.fCountry}</span>
                {Array.isArray(profile.country) && profile.country.length > 0 && (
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: '0.4rem',
                    marginBottom: '0.5rem',
                  }}>
                    {profile.country.map((code) => {
                      const c = COUNTRIES.find((x) => x.code === code);
                      const label = c
                        ? (lang === 'es' ? c.nameEs : c.nameEn)
                        : code;
                      return (
                        <span key={code} style={{
                          ...FONT_SMALL,
                          display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                          background: BG_SHELL, border: `1px solid ${BORDER_DEFAULT}`,
                          borderRadius: 999, padding: '0.2rem 0.6rem',
                          color: TEXT_DEFAULT,
                        }}>
                          {label}
                          <button
                            type="button"
                            onClick={() => set('country', profile.country.filter((x) => x !== code))}
                            aria-label={label}
                            style={{
                              background: 'transparent', border: 'none',
                              color: TEXT_MUTED, cursor: 'pointer',
                              padding: 0, lineHeight: 1, fontSize: '1rem',
                            }}
                          >×</button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <select
                  value=""
                  onChange={(e) => {
                    const code = e.target.value;
                    if (!code) return;
                    const current = Array.isArray(profile.country) ? profile.country : [];
                    if (current.includes(code)) return;
                    set('country', [...current, code]);
                  }}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="" disabled>—</option>
                  {COUNTRIES.filter((c) => {
                    const arr = Array.isArray(profile.country) ? profile.country : [];
                    return !arr.includes(c.code);
                  }).map((c) => (
                    <option key={c.code} value={c.code}>
                      {lang === 'es' ? c.nameEs : c.nameEn}
                    </option>
                  ))}
                </select>
              </div>
              <div style={fieldStyle}>
                <Tooltip text={t.orgLanguageTooltip}>
                  <span style={fieldLabelStyle}>{t.fLanguage}</span>
                </Tooltip>
                <div style={{
                  display: 'flex', border: `1px solid ${BORDER_DEFAULT}`,
                  borderRadius: 6, overflow: 'hidden', width: 'fit-content',
                }}>
                  {['es', 'en'].map((code) => (
                    <button
                      key={code}
                      onClick={() => set('language', code)}
                      style={{
                        ...FONT_SMALL,
                        background: profile.language === code ? TEXT_HEADING : 'transparent',
                        color: profile.language === code ? SURFACE_WHITE : TEXT_MUTED,
                        border: 'none', borderRadius: 0,
                        padding: '0.35rem 0.85rem', cursor: 'pointer',
                        letterSpacing: '0.06em', transition: 'all 0.15s',
                      }}
                    >
                      {code.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ ...fieldStyle, gridColumn: 'span 2' }}>
                <span style={fieldLabelStyle}>{t.fRegions}</span>
                <p style={{
                  ...FONT_TINY,
                  color: TEXT_DISABLED, margin: '0 0 0.6rem', lineHeight: 1.5,
                }}>
                  {t.fRegionsHint}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
                  {(profile.regions || []).map((tag) => (
                    <Chip key={tag} label={regionChipLabel(tag)} onRemove={() => removeRegionTag(tag)} canRemove />
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    list="regions-catalogue"
                    value={newRegionTag}
                    onChange={(e) => setNewRegionTag(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRegionTag(); } }}
                    placeholder={t.addRegionPlaceholder}
                    style={{ ...inputStyle, width: 'auto', flex: 1 }}
                  />
                  <datalist id="regions-catalogue">
                    {flatSubregions.map((sr) => (
                      <option
                        key={sr.code}
                        value={sr.code}
                        label={`${sr.code} — ${lang === 'es' ? sr.nameEs : sr.nameEn} (${sr.country})`}
                      />
                    ))}
                  </datalist>
                  <button onClick={addRegionTag} style={btnPrimary}>{t.addBtn}</button>
                </div>
              </div>
            </div>

            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fOrganizationType}</span>
              <select
                value={profile.organizationType || 'private'}
                onChange={(e) => set('organizationType', e.target.value)}
                style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}
              >
                {ORGANIZATION_TYPES.map((val) => (
                  <option key={val} value={val}>{t.organizationTypeOptions[val]}</option>
                ))}
              </select>
            </div>

            {profile.organizationType !== 'public' && (
              <>
                <div style={fieldStyle}>
                  <span style={fieldLabelStyle}>{t.fPublicSectorExposure}</span>
                  <select
                    value={profile.publicSectorExposure || 'none'}
                    onChange={(e) => set('publicSectorExposure', e.target.value)}
                    style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}
                  >
                    {PUBLIC_SECTOR_EXPOSURE_VALUES.map((val) => (
                      <option key={val} value={val}>{t.publicSectorExposureOptions[val]}</option>
                    ))}
                  </select>
                </div>

                {profile.publicSectorExposure !== 'none' && (
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>{t.fPublicSectorExposureDescription}</span>
                    <textarea
                      value={profile.publicSectorExposureDescription || ''}
                      onChange={(e) => set('publicSectorExposureDescription', e.target.value)}
                      placeholder={t.fPublicSectorExposureDescriptionPlaceholder}
                      rows={3}
                      style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 'inherit' }}
                    />
                    <p style={{
                      ...FONT_TINY,
                      color: TEXT_DISABLED, margin: '0.35rem 0 0', lineHeight: 1.5,
                    }}>
                      {t.fPublicSectorExposureDescriptionHelper}
                    </p>
                  </div>
                )}
              </>
            )}

            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fPdcaStage}</span>
              <select
                value={profile.pdcaStage || 'Plan'}
                onChange={(e) => set('pdcaStage', e.target.value)}
                style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}
              >
                {Object.entries(t.pdcaStageOptions).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>

            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fReviewPeriod}</span>
              <input
                type="number" min={1} max={60}
                value={profile.documentReviewPeriodMonths || 12}
                onChange={(e) => set('documentReviewPeriodMonths', e.target.value ? Number(e.target.value) : 12)}
                style={{ ...inputStyle, width: '8ch' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={fieldStyle}>
                <span style={fieldLabelStyle}>{t.fMrFrequency}</span>
                <input
                  type="number" min={1} max={60}
                  value={profile.mrFrequencyMonths || 12}
                  onChange={(e) => set('mrFrequencyMonths', e.target.value ? Number(e.target.value) : 12)}
                  style={{ ...inputStyle, width: '8ch' }}
                />
              </div>
              <div style={fieldStyle}>
                <span style={fieldLabelStyle}>{t.fAuditFrequency}</span>
                <input
                  type="number" min={1} max={60}
                  value={profile.auditFrequencyMonths || 12}
                  onChange={(e) => set('auditFrequencyMonths', e.target.value ? Number(e.target.value) : 12)}
                  style={{ ...inputStyle, width: '8ch' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={fieldStyle}>
                <span style={fieldLabelStyle}>{t.fRiskThresholdLow}</span>
                <input
                  type="number" min={1} max={25}
                  value={profile.riskMatrix?.thresholds?.low ?? 4}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : '';
                    setDirty(true);
                    setProfile(prev => prev ? {
                      ...prev,
                      riskMatrix: {
                        ...(prev.riskMatrix || {}),
                        thresholds: { ...((prev.riskMatrix || {}).thresholds || {}), low: val },
                      },
                    } : prev);
                  }}
                  style={{ ...inputStyle, width: '8ch' }}
                />
              </div>
              <div style={fieldStyle}>
                <span style={fieldLabelStyle}>{t.fRiskThresholdMedium}</span>
                <input
                  type="number" min={1} max={25}
                  value={profile.riskMatrix?.thresholds?.medium ?? 9}
                  onChange={(e) => {
                    const val = e.target.value ? Number(e.target.value) : '';
                    setDirty(true);
                    setProfile(prev => prev ? {
                      ...prev,
                      riskMatrix: {
                        ...(prev.riskMatrix || {}),
                        thresholds: { ...((prev.riskMatrix || {}).thresholds || {}), medium: val },
                      },
                    } : prev);
                  }}
                  style={{ ...inputStyle, width: '8ch' }}
                />
              </div>
            </div>

            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fRiskAcceptance}</span>
              <select
                value={profile.riskAcceptanceThreshold || 'LOW'}
                onChange={(e) => set('riskAcceptanceThreshold', e.target.value)}
                style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}
              >
                {Object.entries(t.riskAcceptanceOptions).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>


          </>
        )}

        {/* ── Section: ISMS Roles ────────────────────────────────────────── */}
        {activeSection === 'ismsRoles' && (
          <>
            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fIsmsRoleTopMgmt}</span>
              <PeopleSelector
                orgId={org.id}
                lang={lang}
                multiple
                value={Array.isArray(profile.ismsRoles?.topManagementSignatory)
                  ? profile.ismsRoles.topManagementSignatory
                  : (typeof profile.ismsRoles?.topManagementSignatory === 'string' && profile.ismsRoles.topManagementSignatory !== ''
                      ? [profile.ismsRoles.topManagementSignatory]
                      : [])}
                onChange={(v) => setNested('ismsRoles', 'topManagementSignatory', Array.isArray(v) ? v : [])}
              />
            </div>
            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fTopMgmtCommitmentEvidence}</span>
              <div style={{ ...FONT_SMALL, color: TEXT_MUTED, marginBottom: '0.35rem' }}>
                {t.fTopMgmtCommitmentEvidenceHelp}
              </div>
              <EvidenceSelector
                orgId={org.id}
                lang={lang}
                multiple={false}
                value={profile.topManagementCommitmentEvidenceId || null}
                onChange={(v) => set('topManagementCommitmentEvidenceId', v)}
              />
            </div>
            <div style={{ borderTop: `1px solid ${BORDER_DEFAULT}`, margin: '1.25rem 0' }} />
            {[
              { key: 'ismsManager',            label: t.fIsmsRoleIsmsManager, visible: true },
              { key: 'dpo',                    label: t.fIsmsRoleDpo,         visible: (profile.norms || []).includes(NORM_GDPR) },
              { key: 'nis2Contact',            label: t.fIsmsRoleNis2Contact, visible: (profile.norms || []).includes(NORM_NIS2) },
            ].filter((r) => r.visible).map(({ key, label }) => (
              <div key={key} style={fieldStyle}>
                <span style={fieldLabelStyle}>{label}</span>
                <PeopleSelector
                  orgId={org.id}
                  lang={lang}
                  value={profile.ismsRoles?.[key] || null}
                  onChange={(v) => setNested('ismsRoles', key, v || null)}
                />
              </div>
            ))}
            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fIsmsRoleCiso}</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={!!profile.cisoSameAsIsmsManager}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setDirty(true);
                      setProfile((prev) => prev ? {
                        ...prev,
                        ismsRoles: { ...(prev.ismsRoles || {}), ciso: prev.ismsRoles?.ismsManager ?? null },
                        cisoSameAsIsmsManager: true,
                      } : prev);
                    } else {
                      setDirty(true);
                      setProfile((prev) => prev ? { ...prev, cisoSameAsIsmsManager: false } : prev);
                    }
                  }}
                  style={{ accentColor: BRAND_PRIMARY }}
                />
                <span style={{ ...FONT_LABEL, color: TEXT_DEFAULT }}>{t.fCisoSameAsIsmsManager}</span>
              </label>
              <PeopleSelector
                key={profile.ismsRoles?.ismsManager || ''}
                orgId={org.id}
                lang={lang}
                value={profile.cisoSameAsIsmsManager ? (profile.ismsRoles?.ismsManager || null) : (profile.ismsRoles?.ciso || null)}
                onChange={(v) => { if (!profile.cisoSameAsIsmsManager) setNested('ismsRoles', 'ciso', v || null); }}
                disabled={!!profile.cisoSameAsIsmsManager}
              />
            </div>
          </>
        )}

        {/* ── Section: Certification ─────────────────────────────────────── */}
        {activeSection === 'certification' && (
          <>
            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fCertStatus}</span>
              <select
                value={profile.certificationStatus ?? 'none'}
                onChange={(e) => set('certificationStatus', e.target.value)}
                style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}
              >
                {Object.entries(t.certStatusOptions).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>

            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fCertBody}</span>
              <input
                value={profile.certificationBody || ''}
                onChange={(e) => set('certificationBody', e.target.value || null)}
                placeholder="Bureau Veritas"
                style={inputStyle}
              />
            </div>

            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fCertNumber}</span>
              <input
                value={profile.certificateNumber || ''}
                onChange={(e) => set('certificateNumber', e.target.value || null)}
                placeholder="ISO27001-2024-001"
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={fieldStyle}>
                <span style={fieldLabelStyle}>{t.fStage1Date}</span>
                <input
                  type="date"
                  value={profile.stage1AuditDate || ''}
                  onChange={(e) => set('stage1AuditDate', e.target.value || null)}
                  style={inputStyle}
                />
              </div>
              <div style={fieldStyle}>
                <span style={fieldLabelStyle}>{t.fStage2Date}</span>
                <input
                  type="date"
                  value={profile.stage2AuditDate || ''}
                  onChange={(e) => set('stage2AuditDate', e.target.value || null)}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={fieldStyle}>
                <span style={fieldLabelStyle}>{t.fCertDate}</span>
                <input
                  type="date"
                  value={profile.certificationDate || ''}
                  onChange={(e) => set('certificationDate', e.target.value || null)}
                  style={inputStyle}
                />
              </div>
              <div style={fieldStyle}>
                <span style={fieldLabelStyle}>{t.fRecertDate}</span>
                <input
                  type="date"
                  value={profile.recertificationDate || ''}
                  onChange={(e) => set('recertificationDate', e.target.value || null)}
                  style={inputStyle}
                />
              </div>
            </div>
          </>
        )}

        {/* ── Section: Documents ────────────────────────────────────────── */}
        {activeSection === 'documents' && (
          <>
            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fDefaultClass}</span>
              {(profile.documentClassification || []).length === 0 ? (
                <select disabled style={{ ...inputStyle, width: 'auto' }}>
                  <option value="">{t.fDefaultClassEmpty}</option>
                </select>
              ) : (
                <select
                  value={profile.defaultClassification || ''}
                  onChange={(e) => set('defaultClassification', e.target.value)}
                  style={{ ...inputStyle, width: 'auto' }}
                >
                  <option value="">{t.fDefaultClassPlaceholder}</option>
                  {(profile.documentClassification || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              )}
            </div>

            <div style={{ ...fieldStyle, marginBottom: '1.25rem' }}>
              <span style={fieldLabelStyle}>{t.fDocClass}</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
                {(profile.documentClassification || []).map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onRemove={() => removeClassTag(tag)}
                    canRemove={tag !== profile.defaultClassification}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  value={newClassTag}
                  onChange={(e) => setNewClassTag(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addClassTag(); } }}
                  placeholder={t.addClassPlaceholder}
                  style={{ ...inputStyle, width: 'auto', flex: 1 }}
                />
                <button onClick={addClassTag} style={btnPrimary}>{t.addBtn}</button>
              </div>
            </div>
          </>
        )}

        {/* ── Section: Awareness coverage policy ─────────────────────────── */}
        {activeSection === 'awareness' && (
          <>
            <div style={fieldStyle}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!profile.awarenessCoveragePolicy}
                  onChange={(e) => {
                    setDirty(true);
                    if (e.target.checked) {
                      set('awarenessCoveragePolicy', { target: 80, ragGreen: 80, ragAmber: 60 });
                    } else {
                      set('awarenessCoveragePolicy', null);
                    }
                  }}
                  style={{ width: 15, height: 15, cursor: 'pointer', accentColor: BRAND_PRIMARY }}
                />
                <span style={{ ...FONT_BODY, color: TEXT_DEFAULT }}>
                  {t.fAwarenessPolicyEnable}
                </span>
              </label>
            </div>

            {profile.awarenessCoveragePolicy && (
              <>
                <div style={fieldStyle}>
                  <span style={fieldLabelStyle}>{t.fAwarenessTarget}</span>
                  <input
                    type="number" min={0} max={100} step={1}
                    value={profile.awarenessCoveragePolicy.target ?? 80}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 80 : Number(e.target.value);
                      setDirty(true);
                      setProfile(prev => prev ? {
                        ...prev,
                        awarenessCoveragePolicy: {
                          ...(prev.awarenessCoveragePolicy || {}),
                          target: val,
                        },
                      } : prev);
                    }}
                    style={{ ...inputStyle, width: '8ch' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>{t.fAwarenessGreen}</span>
                    <input
                      type="number" min={0} max={100} step={1}
                      value={profile.awarenessCoveragePolicy.ragGreen ?? 80}
                      onChange={(e) => {
                        const val = e.target.value === '' ? 80 : Number(e.target.value);
                        setDirty(true);
                        setProfile(prev => prev ? {
                          ...prev,
                          awarenessCoveragePolicy: {
                            ...(prev.awarenessCoveragePolicy || {}),
                            ragGreen: val,
                          },
                        } : prev);
                      }}
                      style={{ ...inputStyle, width: '8ch' }}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>{t.fAwarenessAmber}</span>
                    <input
                      type="number" min={0} max={100} step={1}
                      value={profile.awarenessCoveragePolicy.ragAmber ?? 60}
                      onChange={(e) => {
                        const val = e.target.value === '' ? 60 : Number(e.target.value);
                        setDirty(true);
                        setProfile(prev => prev ? {
                          ...prev,
                          awarenessCoveragePolicy: {
                            ...(prev.awarenessCoveragePolicy || {}),
                            ragAmber: val,
                          },
                        } : prev);
                      }}
                      style={{ ...inputStyle, width: '8ch' }}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>{t.fAwarenessRed}</span>
                    <span style={{ ...inputStyle, cursor: 'default', color: TEXT_DISABLED, display: 'inline-block', width: 'auto' }}>
                      {t.fAwarenessRedReadonly}
                    </span>
                  </div>
                </div>

                <p style={{
                  ...FONT_SMALL,
                  color: TEXT_DISABLED, margin: '0.4rem 0 0', lineHeight: 1.5,
                }}>
                  {t.helpAwarenessPolicy}
                </p>
              </>
            )}
          </>
        )}

        {/* ── Section: Reference prefixes ────────────────────────────────── */}
        {activeSection === 'refPrefixes' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {[
              { key: 'risk',             label: t.fRefPrefixRisk },
              { key: 'asset',            label: t.fRefPrefixAsset },
              { key: 'supplier',         label: t.fRefPrefixSupplier },
              { key: 'customer',         label: t.fRefPrefixCustomer },
              { key: 'incident',         label: t.fRefPrefixIncident },
              { key: 'audit',            label: t.fRefPrefixAudit },
              { key: 'managementReview', label: t.fRefPrefixManagementReview },
              { key: 'nonconformity',    label: t.fRefPrefixNonconformity },
              { key: 'objective',        label: t.fRefPrefixObjective },
              { key: 'document',         label: t.fRefPrefixDocument },
              { key: 'metric',           label: t.fRefPrefixMetric },
              { key: 'evidence',         label: t.fRefPrefixEvidence },
              { key: 'legalRegister',    label: t.fRefPrefixLegalRegister },
              { key: 'authority',        label: t.fRefPrefixAuthority },
              { key: 'specialInterestGroup', label: t.fRefPrefixSpecialInterestGroup },
            ].map(({ key, label }) => (
              <div key={key} style={fieldStyle}>
                <span style={fieldLabelStyle}>{label}</span>
                <input
                  value={profile.refPrefixes?.[key] ?? ''}
                  onChange={(e) => setNested('refPrefixes', key, e.target.value)}
                  style={inputStyle}
                />
              </div>
            ))}
          </div>
        )}

        {/* ── Section: Technologies ─────────────────────────────────────── */}
        {activeSection === 'technologies' && (
          <div style={{ ...fieldStyle, marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
              {(profile.technologies || []).map((tag) => (
                <Chip key={tag} label={tag} onRemove={() => removeTechTag(tag)} canRemove />
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                value={newTechTag}
                onChange={(e) => setNewTechTag(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTechTag(); } }}
                placeholder={t.addTechPlaceholder}
                style={{ ...inputStyle, width: 'auto', flex: 1 }}
              />
              <button onClick={addTechTag} style={btnPrimary}>{t.addBtn}</button>
            </div>
          </div>
        )}

        {/* ── Section: AI Context ───────────────────────────────────────── */}
        {activeSection === 'ai' && (
          <>
            <div style={fieldStyle}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!profile.aiProcessingConsent}
                  onChange={(e) => toggleAiConsent(e.target.checked)}
                  style={{ width: 15, height: 15, cursor: 'pointer', accentColor: BRAND_PRIMARY }}
                />
                <span style={{ ...FONT_BODY, color: TEXT_DEFAULT }}>
                  {t.fAiProcessingConsent}
                </span>
              </label>
              {profile.aiProcessingConsent && profile.aiProcessingConsentDate && (
                <div style={{ marginTop: '0.5rem', marginLeft: '1.6rem' }}>
                  <span style={fieldLabelStyle}>{t.fAiProcessingConsentDate}</span>
                  <span style={{
                    ...FONT_LABEL, color: TEXT_DEFAULT,
                  }}>
                    {profile.aiProcessingConsentDate.slice(0, 10)}
                  </span>
                </div>
              )}
              <p style={{
                ...FONT_SMALL,
                color: TEXT_DISABLED, margin: '0.4rem 0 0', lineHeight: 1.5,
              }}>
                {t.aiProcessingConsentHelper}
              </p>
            </div>

            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>{t.fAiContext}</span>
              <textarea
                value={profile.aiContext || ''}
                onChange={(e) => set('aiContext', e.target.value)}
                rows={5}
                placeholder={t.fAiContextPlaceholder}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
              />
              <p style={{
                ...FONT_SMALL,
                color: TEXT_DISABLED, margin: '0.4rem 0 0', lineHeight: 1.5,
              }}>
                {t.fAiContextHelper}
              </p>
            </div>
          </>
        )}

        {/* ── Section: People ───────────────────────────────────────────── */}
        {activeSection === 'people' && showPeopleImport && (
          <ImportWizard
            lang={lang}
            dedupeKey="email"
            onClose={() => setShowPeopleImport(false)}
            onImport={async (rows) => {
              const now = new Date().toISOString();
              const updated = [...people];
              rows.forEach(row => {
                let existingIdx = row.email
                  ? updated.findIndex(p => p.email && p.email.toLowerCase() === row.email.toLowerCase())
                  : -1;
                if (existingIdx === -1 && !row.email && row.name) {
                  existingIdx = updated.findIndex(p => p.name && p.name.toLowerCase().trim() === row.name.toLowerCase().trim());
                }
                if (existingIdx >= 0) {
                  const existing = updated[existingIdx];
                  updated[existingIdx] = {
                    ...existing,
                    name:               row.name       || existing.name,
                    email:              row.email      || existing.email,
                    role:               row.role       !== null ? row.role       : existing.role,
                    department:         row.department !== null ? row.department : existing.department,
                    type:               row.type       !== null ? row.type       : existing.type,
                    country:            row.country    !== null ? row.country    : existing.country,
                    city:               row.city       !== null ? row.city       : existing.city,
                    timezone:           row.timezone   !== null ? row.timezone   : existing.timezone,
                    isResponsibilityRole: row.isResponsibilityRole != null ? row.isResponsibilityRole : existing.isResponsibilityRole,
                    updatedAt:          now,
                  };
                } else {
                  updated.push({
                    id:                   nextPersonId(updated),
                    name:                 row.name,
                    email:                row.email      || null,
                    role:                 row.role       || null,
                    department:           row.department || null,
                    type:                 row.type       || null,
                    country:              row.country    || null,
                    city:                 row.city       || null,
                    timezone:             row.timezone   || null,
                    isResponsibilityRole: !!row.isResponsibilityRole,
                    reportsTo:            null,
                    active:               true,
                    createdAt:            now,
                    updatedAt:            now,
                  });
                }
              });
              await writeOrgFile(org.id, 'people.json', {
                orgId: org.id, people: updated, updatedAt: now,
              });
              setPeople(updated);
              setPeopleDirty(false);
              return rows.length;
            }}
            columns={[
              { key: 'name',                 label: 'Name',                  required: true,  type: 'string' },
              { key: 'email',                label: 'Email',                 required: false, type: 'string', default: null },
              { key: 'role',                 label: 'Role',                  required: false, type: 'string', default: null },
              { key: 'department',           label: 'Department',            required: false, type: 'string', default: null },
              { key: 'type',                 label: 'Type',                  required: false, type: 'string', default: null, enum: ['employee', 'contractor', 'external'], caseInsensitive: true },
              { key: 'country',              label: 'Country',               required: false, type: 'string', default: null },
              { key: 'city',                 label: 'City',                  required: false, type: 'string', default: null },
              { key: 'timezone',             label: 'Timezone',              required: false, type: 'string', default: null, ianaTimezone: true },
              { key: 'isResponsibilityRole', label: 'IsResponsibilityRole',  required: false, type: 'boolean', default: false },
            ]}
            templateFilename="import-people.csv"
            title={{ es: t.importPeopleTitle, en: 'Import people' }}
          />
        )}

        {activeSection === 'people' && !showPeopleImport && (
          <>
            {/* Add person / Import CSV buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginBottom: '1rem' }}>
              <button
                onClick={() => setShowPeopleImport(true)}
                disabled={peopleView === 'form'}
                style={{
                  ...BTN_SECONDARY,
                  ...FONT_BUTTON,
                  borderRadius: 6, padding: '0.45rem 0.9rem', whiteSpace: 'nowrap',
                  opacity: peopleView === 'form' ? 0.5 : 1,
                  cursor: peopleView === 'form' ? 'default' : 'pointer',
                }}
              >
                {t.importCsv}
              </button>
              <button
                onClick={openAddForm}
                disabled={peopleView === 'form'}
                style={{
                  ...BTN_PRIMARY,
                  ...FONT_BUTTON,
                  border: 'none',
                  borderRadius: 8, padding: '0.5rem 1.1rem',
                  cursor: peopleView === 'form' ? 'default' : 'pointer',
                  opacity: peopleView === 'form' ? 0.5 : 1,
                }}
              >
                + {t.addPerson}
              </button>
            </div>

            {/* Bulk action bar */}
            {selectedIds.size > 0 && peopleView !== 'form' && (() => {
              const selectedPeople = people.filter((p) => selectedIds.has(p.id));
              const hasActive   = selectedPeople.some((p) => p.active === true);
              const hasInactive = selectedPeople.some((p) => p.active === false);
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.5rem 0.75rem', marginBottom: '0.6rem',
                  background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
                }}>
                  <span style={{ ...FONT_LABEL, color: BRAND_PRIMARY }}>
                    {t.nSelected.replace('{n}', selectedIds.size)}
                  </span>
                  {hasActive && (
                    <button
                      onClick={handleDeactivateSelected}
                      style={{ ...btnSmall, border: BTN_DESTRUCTIVE_OUTLINE.border, color: BTN_DESTRUCTIVE_OUTLINE.color }}
                    >
                      {t.deactivateSelected}
                    </button>
                  )}
                  {hasInactive && (
                    <button
                      onClick={handleReactivateSelected}
                      style={btnSmall}
                    >
                      {t.reactivateSelected}
                    </button>
                  )}
                  <button
                    onClick={handleDeleteSelected}
                    disabled={scanningDelete}
                    style={{
                      ...btnSmall,
                      border: BTN_DESTRUCTIVE_OUTLINE.border,
                      color: BTN_DESTRUCTIVE_OUTLINE.color,
                      opacity: scanningDelete ? 0.6 : 1,
                      cursor: scanningDelete ? 'default' : 'pointer',
                    }}
                  >
                    {scanningDelete ? t.scanningPeople : t.deleteSelected}
                  </button>
                </div>
              );
            })()}

            {/* People table */}
            {peopleView !== 'form' && (people.length === 0 ? (
              <p style={{
                ...FONT_LABEL,
                color: TEXT_DISABLED, margin: '0 0 1.5rem',
              }}>
                {t.noPeople}
              </p>
            ) : (
              <div style={{ marginBottom: '1.5rem' }}>
                <table style={{
                  ...FONT_LIST_ITEM,
                  width: '100%', borderCollapse: 'collapse',
                }}>
                  <thead>
                    <tr>
                      <th style={{
                        ...FONT_TINY,
                        textAlign: 'center', color: TEXT_DISABLED,
                        padding: '0.4rem 0.5rem', borderBottom: `1px solid ${BORDER_DEFAULT}`,
                        width: 36,
                      }}>
                        <input
                          type="checkbox"
                          title={t.selectAll}
                          checked={people.length > 0 && selectedIds.size === people.length}
                          ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < people.length; }}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedIds(new Set(people.map((p) => p.id)));
                            else setSelectedIds(new Set());
                          }}
                        />
                      </th>
                      {[t.colName, t.colRole, t.colStatus, t.colActions].map((h) => (
                        <th key={h} style={{
                          ...FONT_TINY,
                          textAlign: 'left', color: TEXT_DISABLED,
                          letterSpacing: '0.04em', textTransform: 'uppercase',
                          padding: '0.4rem 0.75rem', borderBottom: `1px solid ${BORDER_DEFAULT}`,
                          fontWeight: 600,
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {people.map((p, idx) => (
                      <tr key={p.id} style={{ background: idx % 2 === 0 ? BG_SURFACE : 'transparent' }}>
                        <td style={{ padding: '0.55rem 0.5rem', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(p.id)}
                            onChange={(e) => {
                              setSelectedIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(p.id); else next.delete(p.id);
                                return next;
                              });
                            }}
                          />
                        </td>
                        <td style={{ padding: '0.55rem 0.75rem', color: TEXT_HEADING, fontWeight: 600 }}>
                          {p.name}
                        </td>
                        <td style={{ padding: '0.55rem 0.75rem', color: TEXT_DEFAULT }}>
                          {p.role || '—'}
                        </td>
                        <td style={{ padding: '0.55rem 0.75rem' }}>
                          <span style={{
                            ...FONT_SMALL,
                            display: 'inline-block', borderRadius: 12,
                            padding: '0.15rem 0.55rem',
                            background: p.active ? STATUS_GOOD.bg : BG_MUTED,
                            color:      p.active ? BRAND_PRIMARY : TEXT_MUTED,
                          }}>
                            {p.active ? t.statusActive : t.statusInactive}
                          </span>
                        </td>
                        <td style={{ padding: '0.55rem 0.75rem' }}>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            <button onClick={() => openEditForm(p)} style={btnSmall}>
                              {t.editPerson}
                            </button>
                            <button
                              onClick={() => togglePersonActive(p)}
                              style={{
                                ...btnSmall,
                                border: p.active ? BTN_STATUS_FLAG.border : BTN_SECONDARY.border,
                                color: p.active ? BTN_STATUS_FLAG.color : BTN_SECONDARY.color,
                              }}
                            >
                              {p.active ? t.deactivate : t.reactivate}
                            </button>
                            <button
                              onClick={() => handleDeletePerson(p)}
                              disabled={scanningDelete}
                              style={{
                                ...btnSmall,
                                border: BTN_DESTRUCTIVE_OUTLINE.border,
                                color: BTN_DESTRUCTIVE_OUTLINE.color,
                                opacity: scanningDelete ? 0.6 : 1,
                                cursor: scanningDelete ? 'default' : 'pointer',
                              }}
                            >
                              {scanningDelete ? t.scanningPeople : t.deletePerson}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

            {/* Inline add / edit form */}
            {peopleView === 'form' && (
              <div style={{
                background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`,
                borderRadius: 10, padding: '1.5rem',
              }}>
                <div style={{
                  ...FONT_BODY_STRONG, fontWeight: 700,
                  color: TEXT_HEADING, marginBottom: '1.25rem',
                }}>
                  {editingPerson ? t.editPersonTitle : t.newPersonTitle}
                </div>

                {/* Name — required */}
                <div style={fieldStyle}>
                  <span style={fieldLabelStyle}>
                    {t.fPersonName} <span style={{ color: ERROR_TEXT }}>*</span>
                  </span>
                  <input
                    value={personForm.name}
                    onChange={(e) => {
                      setPersonForm((f) => ({ ...f, name: e.target.value }));
                      setPersonNameErr(false);
                    }}
                    style={{ ...inputStyle, borderColor: personNameErr ? FIELD_ERROR_BORDER : BORDER_DEFAULT }}
                  />
                  {personNameErr && (
                    <span style={{
                      ...FONT_TINY, color: ERROR_TEXT,
                    }}>
                      {t.nameRequired}
                    </span>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>
                      {t.fPersonEmail} <span style={{ color: ERROR_TEXT }}>*</span>
                    </span>
                    <input
                      value={personForm.email}
                      onChange={(e) => {
                        setPersonForm((f) => ({ ...f, email: e.target.value }));
                        setEmailTouched(true);
                        setEmailErr(null);
                      }}
                      style={{ ...inputStyle, borderColor: emailErr ? FIELD_ERROR_BORDER : BORDER_DEFAULT }}
                    />
                    {emailErr === 'required' && (
                      <span style={{ ...FONT_TINY, color: ERROR_TEXT }}>
                        {t.emailRequired}
                      </span>
                    )}
                    {emailErr === 'duplicate' && (
                      <span style={{ ...FONT_TINY, color: ERROR_TEXT }}>
                        {t.emailDuplicate}
                      </span>
                    )}
                  </div>
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>{t.fPersonRole}</span>
                    <input
                      value={personForm.role}
                      onChange={(e) => setPersonForm((f) => ({ ...f, role: e.target.value }))}
                      style={inputStyle}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>{t.fPersonDept}</span>
                    <input
                      value={personForm.department}
                      onChange={(e) => setPersonForm((f) => ({ ...f, department: e.target.value }))}
                      style={inputStyle}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>{t.fPersonCountry}</span>
                    <select
                      value={personForm.country}
                      onChange={(e) => setPersonForm((f) => ({ ...f, country: e.target.value }))}
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      <option value="">—</option>
                      {personForm.country !== ''
                        && !COUNTRIES.some((c) => c.code === personForm.country) && (
                        <option value={personForm.country}>
                          {personForm.country}{lang === 'es' ? ' (valor anterior)' : ' (legacy value)'}
                        </option>
                      )}
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {lang === 'es' ? c.nameEs : c.nameEn}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>{t.fPersonCity}</span>
                    <input
                      value={personForm.city}
                      onChange={(e) => setPersonForm((f) => ({ ...f, city: e.target.value }))}
                      style={inputStyle}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>{t.fPersonTimezone}</span>
                    <input
                      list="iana-timezones"
                      value={personForm.timezone}
                      onChange={(e) => {
                        setPersonForm((f) => ({ ...f, timezone: e.target.value }));
                        setTimezoneWarn(false);
                      }}
                      style={inputStyle}
                    />
                    {timezoneWarn && (
                      <span style={{ ...FONT_TINY, color: UNSAVED_AMBER }}>
                        {t.timezoneWarning}
                      </span>
                    )}
                  </div>
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>{t.fPersonType}</span>
                    <select
                      value={personForm.type || ''}
                      onChange={(e) => setPersonForm((f) => ({ ...f, type: e.target.value }))}
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      <option value="">—</option>
                      {Object.entries(t.personTypeOptions).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>{t.fStartDate}</span>
                    <input
                      type="date"
                      value={personForm.startDate}
                      onChange={(e) => setPersonForm((f) => ({ ...f, startDate: e.target.value }))}
                      style={inputStyle}
                    />
                  </div>
                  <div style={fieldStyle}>
                    <span style={fieldLabelStyle}>{t.fEndDate}</span>
                    <input
                      type="date"
                      value={personForm.endDate}
                      onChange={(e) => setPersonForm((f) => ({ ...f, endDate: e.target.value }))}
                      style={inputStyle}
                    />
                  </div>
                </div>
                <datalist id="iana-timezones">
                  {(() => {
                    let tzs = [];
                    try { tzs = Intl.supportedValuesOf('timeZone'); } catch { tzs = []; }
                    return tzs.map((tz) => <option key={tz} value={tz} />);
                  })()}
                </datalist>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!personForm.isResponsibilityRole}
                      onChange={(e) => setPersonForm((f) => ({ ...f, isResponsibilityRole: e.target.checked }))}
                      style={{ width: 15, height: 15, cursor: 'pointer', accentColor: BRAND_PRIMARY }}
                    />
                    <span style={{ ...FONT_SMALL, color: TEXT_DISABLED, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      {t.fIsResponsibilityRole}
                    </span>
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.25rem' }}>
                  <button
                    onClick={savePersonForm}
                    style={{
                      ...BTN_PRIMARY,
                      ...FONT_BUTTON,
                      border: 'none',
                      borderRadius: 8, padding: '0.5rem 1.1rem', cursor: 'pointer',
                    }}
                  >
                    {t.savePerson}
                  </button>
                  <button
                    onClick={cancelPersonForm}
                    style={{
                      ...BTN_SECONDARY,
                      ...FONT_BUTTON,
                      borderRadius: 8, padding: '0.5rem 1.1rem', cursor: 'pointer',
                    }}
                  >
                    {t.cancelPerson}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Section: Reopen closed records (§11 rule 35) ───────────────── */}
        {activeSection === 'reopen' && (
          <div>
            <p style={{
              ...FONT_LABEL,
              color: TEXT_MUTED, lineHeight: 1.55, margin: '0 0 1.5rem',
            }}>
              {t.reopenHelper}
            </p>

            {closedRecords === null && (
              <p style={{ ...FONT_LABEL, color: TEXT_MUTED }}>
                {t.reopenLoading}
              </p>
            )}

            {closedRecords !== null && [
              { type: 'nonconformity',    heading: t.reopenNcGroup  },
              { type: 'incident',         heading: t.reopenIncGroup },
              { type: 'audit',            heading: t.reopenAudGroup },
              { type: 'managementReview', heading: t.reopenMrGroup  },
              { type: 'document',         heading: t.reopenDocGroup },
            ].map(({ type, heading }) => (
              <div key={type} style={{ marginBottom: '1.5rem' }}>
                <h3 style={{
                  ...FONT_BODY_STRONG, fontWeight: 700,
                  color: TEXT_HEADING, margin: '0 0 0.6rem',
                }}>{heading}</h3>
                {closedRecords[type].length === 0 ? (
                  <p style={{
                    ...FONT_LABEL,
                    color: TEXT_DISABLED, margin: 0,
                  }}>{t.reopenEmptyGroup}</p>
                ) : (
                  <table style={{
                    width: '100%', borderCollapse: 'collapse',
                    background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 6,
                  }}>
                    <tbody>
                      {closedRecords[type].map((record) => (
                        <tr key={record.id} style={{ borderBottom: `1px solid ${BG_SHELL}` }}>
                          <td style={{
                            ...FONT_LABEL,
                            padding: '0.55rem 0.75rem',
                            color: TEXT_HEADING, whiteSpace: 'nowrap',
                          }}>{record.ref || record.id}</td>
                          <td style={{
                            ...FONT_LIST_ITEM,
                            padding: '0.55rem 0.75rem',
                            color: TEXT_DEFAULT,
                          }}>
                            {type === 'managementReview'
                              ? (record.date || record.id)
                              : (record.title || record.description || record.id)}
                          </td>
                          <td style={{ padding: '0.35rem 0.75rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <button
                              onClick={() => setReopenModal({ record, entityType: type, reason: '', error: null, saving: false })}
                              style={{
                                ...BTN_DESTRUCTIVE_OUTLINE,
                                ...FONT_SMALL,
                                borderRadius: 6,
                                padding: '0.3rem 0.7rem', cursor: 'pointer',
                              }}
                            >
                              {t.reopenBtn}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}

        </fieldset>

      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || 'Confirm'}
        cancelLabel={t.formCancel}
        secondaryAction={confirmState?.secondaryAction}
        confirmColor={confirmState?.confirmColor}
      />

      {/* §11 rule 35 — reopen confirmation modal with free-text reason ──── */}
      {reopenModal && (
        <div
          onClick={() => !reopenModal.saving && setReopenModal(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 34, 53, 0.45)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10,
              padding: '1.75rem 2rem', maxWidth: 460, width: '90%',
            }}
          >
            <h3 style={{
              ...FONT_BODY_STRONG, fontWeight: 700,
              color: TEXT_HEADING, margin: '0 0 0.5rem',
            }}>
              {t.reopenModalTitle}
            </h3>
            <p style={{
              ...FONT_LABEL,
              color: TEXT_MUTED, lineHeight: 1.55, margin: '0 0 1rem',
            }}>
              {t.reopenModalHelper}
            </p>
            <label style={{ ...fieldLabelStyle, marginBottom: '0.3rem' }}>
              {t.reopenReasonLabel}
            </label>
            <textarea
              value={reopenModal.reason}
              onChange={(e) => setReopenModal(prev => prev ? { ...prev, reason: e.target.value, error: null } : prev)}
              disabled={reopenModal.saving}
              rows={4}
              autoFocus
              style={{
                ...FONT_BODY,
                width: '100%', padding: '0.5rem 0.7rem', boxSizing: 'border-box',
                border: `1px solid ${reopenModal.error === 'reason-required' ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
                borderRadius: 6, background: SURFACE_WHITE,
                color: TEXT_HEADING, resize: 'vertical', lineHeight: 1.5,
              }}
            />
            {reopenModal.error === 'reason-required' && (
              <p style={{
                ...FONT_SMALL,
                color: ERROR_TEXT, margin: '0.3rem 0 0',
              }}>{t.reopenReasonRequired}</p>
            )}
            {reopenModal.error === 'save-failed' && (
              <p style={{
                ...FONT_SMALL,
                color: ERROR_TEXT, margin: '0.3rem 0 0',
              }}>{t.reopenError}</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button
                type="button"
                onClick={() => setReopenModal(null)}
                disabled={reopenModal.saving}
                style={{
                  ...BTN_SECONDARY,
                  ...FONT_BUTTON,
                  borderRadius: 8,
                  padding: '0.5rem 1.1rem',
                  cursor: reopenModal.saving ? 'not-allowed' : 'pointer',
                }}
              >{t.reopenCancelBtn}</button>
              <button
                type="button"
                onClick={handleReopenConfirm}
                disabled={reopenModal.saving}
                style={{
                  ...BTN_PRIMARY,
                  ...FONT_BUTTON, fontWeight: 700,
                  border: 'none',
                  borderRadius: 8,
                  padding: '0.5rem 1.1rem',
                  cursor: reopenModal.saving ? 'not-allowed' : 'pointer',
                }}
              >{t.reopenConfirmBtn}</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
